import * as enumerations from './enumerations';

export interface NetworkSynchronizable {
    readonly Custom: {
        readonly status: enumerations.RequestStatus;
        readonly expires: Date;
    };
}

export interface NetworkObject<T> extends NetworkSynchronizable {
    readonly Data: T | undefined;
}

export interface NetworkEntity extends NetworkSynchronizable {
    readonly Custom: NetworkSynchronizable['Custom'] & {
        readonly internalID: number;
        readonly status: enumerations.RequestStatus;
    };
}

export interface Entity<ET extends string = string, ID extends number | string = number> extends NetworkEntity {
    readonly EntityType: ET;
    readonly ID: ID;
}

export interface SortableEntity<ET extends string = string> extends Entity<ET> {
    readonly Sort: number;
}

export interface NestableEntity<ET extends string = string> extends SortableEntity<ET> {
    readonly Name: string;
    readonly FatherID: number | null;
    readonly ScopeEndSort: number;
    readonly DepthLevel: number;
}

export interface SystemEnumerationEntity<ET extends string = string, ID extends number | string = number> extends Entity<ET, ID> {
    readonly Identifier: string;
    readonly Description: string;
}

export interface NamedEntity<ET extends string = string> extends Entity<ET> {
    readonly NameFI: string;
    readonly NameEN: string;
    readonly NameSE: string;
    readonly Name: string;
}

export interface UserTrackingEntity<ET extends string = string> extends Entity<ET> {
    readonly OwnerID: number;
    readonly CreatorID: number;
    readonly Created: Date;
    readonly Updated: Date;
}

export interface NetworkArray<T extends Entity<ET, ID>, ID extends number | string = number, ET extends string = string> extends NetworkSynchronizable {
    readonly Data: T[];
}

export interface NetworkPaginationSlice<T extends Entity> extends NetworkArray<T> {
    readonly Count: number;
    readonly TotalCount: number;
    readonly NextID?: T['ID'];
    readonly Order?: enumerations.SortOrder;
}

export interface NestedNetworkPaginationSlice<T extends Entity> extends NetworkArray<T> {
    readonly FatherID: number;
    readonly Count: number;
    readonly TotalCount: number;
    readonly NextRow?: number;
    readonly Order?: enumerations.SortOrder;
}

// TODO generate from swagger stuff

export interface EntityChange {
    readonly EntityType: string;
    readonly EntityID: number;
    readonly CompanyID: number;
    readonly UserID: number;
    readonly ChangeType: number;
    readonly TimeStamp: Date;
    readonly FriendlyType: string;
    readonly FriendlyName: string;
    readonly OptionalDetails: { [key: string]: string; };
    readonly TargetUserIDs: number[];
}

export interface FunctionalityAccess extends Entity<'FunctionalityAccess'> {
    readonly FunctionalityID: enumerations.AccessFunctionality;
    readonly AccessTypeID: enumerations.AccessType;
}

export interface StrategyExecutionType extends SystemEnumerationEntity<'StrategyExecutionType', enumerations.StrategyExecutionType> { }
export interface StrategyPublishingLevel extends SystemEnumerationEntity<'StrategyExecutionType', enumerations.StrategyPublishingLevel> { }
export interface StrategyAllowUserInviteType extends SystemEnumerationEntity<'StrategyAllowUserInviteType', enumerations.StrategyAllowUserInviteType> { }

export interface QuickStartStep extends Entity<'QuickStartStep'> {
    readonly QuickStartStepCategoryID: number;
    readonly Sort: number;
    readonly SystemModuleUriTypeID: enumerations.SystemModuleUriType;
    readonly ICProductTourIdentifier: number | null;

    readonly Title: string;
    readonly Description: string;
}

export interface QuickStartStepCategoryReport extends Entity<'QuickStartStepCategoryReport'> {
    readonly QuickStartID: number;
    readonly Sort: number;

    readonly Title: string;
    readonly Description: string;

    readonly Steps: QuickStartStep[];
}

export interface QuickStartReport extends Entity<'QuickStartReport'> {
    readonly Title: string;
    readonly StepCategories: QuickStartStepCategoryReport[];
}

export interface Session extends Entity<'Session'> {
    readonly ServiceVersion: string;
    readonly UserID: number;
    readonly UserName: string;
    readonly CurrentCompanyID: number;
    readonly CurrentCompanyName: string;
    readonly IsUserAdministrator: boolean;
    readonly IsUser4SAdministrator: boolean;
    readonly IsPartnerCompanyAdministrator: boolean;
    readonly FunctionalityAccesses: FunctionalityAccess[];
    readonly FunctionalityAddOns: enumerations.AccessFunctionalityAddOn[];
    readonly Language: string;
    readonly CurrentLanguageID: enumerations.SystemLanguage;
    readonly HasTemporaryAlertAccess: boolean;
    readonly HasTemporaryWorkshopAccess: boolean;
    readonly HomePageID: number | null;
    readonly OverrideLogoURI: string;
    readonly OverrideTopColorHex: string;
    readonly HasPartnerPanel: boolean;
    readonly AdvisorID: number;
    readonly AdvisorName: string;
    readonly AdvisorLogoURI: string;
    readonly AdvisorPromotionalText: string;
    readonly AdvisorPromotionalURI: string;
    readonly DefaultLoginCompanyID: number;
    readonly CompanyTemplateTypeID: number;
    readonly PartnerCompanyID: number;
    readonly PartnerCompanyName: number;
    readonly OriginalSessionFromUserID: number;
    readonly UserFunctionID: number;
    readonly CashflowForecastEnabled: boolean;
    readonly Is4SCompany: boolean;
    readonly ProductPackageID: enumerations.ProductPackage;
    readonly StrategyOnboardingDone: boolean;
    readonly StrategyExecutionTypeID: number | null;
    readonly StrategyPublishingLevelID: number | null;
    readonly StrategyAllowNotAlignedBreakthrough: boolean;
    readonly StrategyAllowUserInviteTypeID: number | null;
    readonly PublicPagesEnabled: boolean;
    readonly IsPublicPageUser: boolean;
    readonly PublicPageID: number | null;
    readonly PublicPageCreatedByUserID: number | null;
    readonly IsStrategyFacilitator: boolean;
    readonly CustomerGeneralSatisfaction: boolean;
    readonly UserRoleBillingUserTypeID: number | null;
    readonly CompanyHasInitiatives: boolean;
    readonly QuickStartID: number | null;
    readonly QuickStartEndDate: Date | null;
    readonly HasMobileToken: boolean;
    readonly IsTrialUnknownUser: boolean;
    readonly InitialStartingPoint1: number | null;
    readonly InitialStartingPoint2: number | null;
    readonly WorkshopLanguageCode: string;
    readonly IsExternalUser: boolean;
    readonly UseUserRoleBasedBilling: boolean;
    readonly AllowCustomTaspaExporting: boolean;
    readonly DisableWhereby: boolean;

    readonly CanUserAddStrategy: boolean;

    readonly Custom: Entity<'Session'>['Custom'] & {
        readonly valid: boolean;
    };
}

export interface GeneralSettings extends Entity<'GeneralSettings'> {
    readonly CompanyLogoURI: string;
    readonly FiscalPeriodStartMonth: number;
    readonly LastFinancialPeriodTypeID: enumerations.LastFinancialPeriodType;
    readonly LastFinancialPeriodRollingMonthOffset: number | null;
    readonly LastFinancialPeriodRollingDay: number | null;
    readonly LastFinancialPeriodDate: Date | null;
    readonly CorporateCalculation: boolean;
    readonly AdvisorPromotionalText: string;
    readonly AdvisorPromotionalURI: string;
    readonly MicrosoftTenantIdentifier: string;
    readonly DefaultLanguageID: enumerations.SystemLanguage;
    readonly GlobalErrorMessage: string;
    readonly IndicatorCalculationRunning: boolean;
    readonly IndicatorValidationRunning: boolean;
    readonly LastSalesPeriodTypeID: enumerations.LastFinancialPeriodType;
    readonly LastSalesPeriodRollingMonthOffset: number | null;
    readonly LastSalesPeriodRollingDay: number | null;
    readonly LastSalesPeriodDate: Date | null;
    readonly RequestMilestoneStatusUpdates: boolean;
    readonly StrategyOnboardingDone: boolean;
    readonly StrategyExecutionTypeID: enumerations.StrategyExecutionType;
    readonly StrategyPublishingLevelID: enumerations.StrategyPublishingLevel;
    readonly StrategyAllowNotAlignedBreakthrough: boolean;
    readonly PublicPagesEnabled: boolean;
    readonly MilestoneStatusUpdateDay: number;
    readonly StrategyTertialPeriodStartMonth: number;
    readonly StrategyQuarterPeriodStartMonth: number;
    readonly GoalInstructionText: string;
    readonly GoalInstructionTextEN: string;
    readonly GoalInstructionTextFI: string;
    readonly MilestoneInstructionText: string;
    readonly MilestoneInstructionTextEN: string;
    readonly MilestoneInstructionTextFI: string;
    readonly StatusUpdateInstructionText: string;
    readonly StatusUpdateInstructionTextEN: string;
    readonly StatusUpdateInstructionTextFI: string;
    readonly BusinessReviewInstructionText: string;
    readonly BusinessReviewInstructionTextEN: string;
    readonly BusinessReviewInstructionTextFI: string;
    readonly DefaultStrategyNamingTypeID: enumerations.StrategyNamingType;
    readonly DefaultStrategicProgramUpdateFrequencyID: enumerations.StrategicProgramUpdateFrequency;
    readonly TrackMilestoneStatusUpdatesOnPeriodLevel: boolean;
}

export interface UserSyncExcelSettings extends Entity<'UserSyncExcelSettings'> {
    readonly IsActive: boolean;
    readonly UseExternalIDForUsers: boolean;
    readonly UseExternalIDForTeams: boolean;
    readonly LastUpdate: Date | null;
    readonly LastError: string;
    readonly ImportInProgress: boolean;

    readonly Custom: Entity<'UserSyncExcelSettings'>['Custom'] & {
        readonly syncPending: boolean;
    };
}

export interface PublicExternalLinkType extends SystemEnumerationEntity<'PublicExternalLinkType', enumerations.PublicExternalLinkType> { }

export interface PublicExternalLink extends Entity<'PublicExternalLink'> {
    readonly CompanyID: number;
    readonly CreatedBy: number;
    readonly Created: Date;
    readonly TypeID: enumerations.PublicExternalLinkType;
    readonly Title: string;
    readonly IdentifierGuid: string;
    readonly LanguageID: number;
    readonly RefreshTimeout: number | null;
    readonly DashboardPageID: number | null;
    readonly PageFilterDataJSON: string;
    readonly AlertID: number | null;
    readonly WorkshopID: number | null;
    readonly DialogScheduleBatchID: number | null;

    readonly PageUrl: string;
}

export interface LastFinancialPeriodType extends SystemEnumerationEntity<'LastFinancialPeriodType', enumerations.LastFinancialPeriodType> {}

export interface SystemLanguage extends SystemEnumerationEntity<'SystemLanguage', enumerations.SystemLanguage> {
    readonly ISOCode: string;
}

export interface Company extends Entity<'Company'> {
    readonly Name: string;
    readonly PartnerSupportLevelID: enumerations.PartnerSupportLevel;
    readonly BillingCorporateCalculationOn: boolean;
    readonly BillingCustomAPI: boolean;
    readonly BillingSeveraIntegration: boolean;
    readonly AgreementSigned: Date | null;
    readonly AskForAgreementSigning: boolean;
}

export interface UserCompanyAccess extends Entity<'UserCompanyAccess'> {
    readonly UserID: number;
    readonly CompanyID: number;
    readonly CompanyName: string;
    readonly SystemUserTypeID: enumerations.SystemUserType;
    readonly SystemUserRoleID: enumerations.SystemUserRole;
    readonly UserRoleBillingUserTypeID: enumerations.UserRoleBillingUserType | null;
    readonly IsStrategyFacilitator: boolean;
    readonly IsUserAdministrator: enumerations.DBBoolean;
    readonly IsExternalUser: boolean;
}

export interface PartnerSupportLevel extends SystemEnumerationEntity<'PartnerSupportLevel', enumerations.PartnerSupportLevel> { }

export interface PartnerCompany extends Entity<'PartnerCompany'> {
    readonly Name: string;
    readonly PartnerAgreementSignedDate: Date | null;
    readonly PartnerAgreementSignedByUser: string | null;
}

export interface UserPartnerAccessReport extends Entity<'UserPartnerAccessReport'> {
    readonly UserID: number;
    readonly Name: string;
    readonly Email: string;
    readonly IsAdmin: boolean;
    readonly AccessibleCompanies: number[];
}

export interface PartnerClientCompanyType extends SystemEnumerationEntity<'PartnerClientCompanyType', enumerations.PartnerClientCompanyType> { }

export interface PartnerClientCompanyReport extends Entity<'PartnerClientCompanyReport'> {
    readonly Name: string;
    readonly TypeID: enumerations.PartnerClientCompanyType;
    readonly SupportLevelID: enumerations.PartnerSupportLevel;
    readonly PerformanceManagementAlertEvaluationInterval: number | null;
    readonly Accessible: boolean;
    readonly UserCount: number;
    readonly FixedMonthlyServicePrice: number;
    readonly CustomerSatisfactionScore: number | null;
    readonly UserActivenessScore: number | null;
    readonly Events: PartnerClientCompanyEvent[];
}

export interface PartnerClientCompanyEventType extends SystemEnumerationEntity<'PartnerClientCompanyEventType', enumerations.PartnerClientCompanyEventType> {
    readonly Color: string;
}

export interface PartnerClientCompanyEvent extends Entity<'PartnerClientCompanyEvent'> {
    readonly TypeID: enumerations.PartnerClientCompanyEventType;
    readonly Date: Date;
    readonly Name: string;
    readonly Comments: string[];
}

export interface PerformanceIndicatorPeriodType extends SystemEnumerationEntity<'PerformanceIndicatorPeriodType', enumerations.PeriodType> {
    readonly Adverb: string;
    readonly Adjective: string;
    readonly Abbreviation: string;
    readonly NPeriods: string;
}

export interface PerformanceIndicatorValueDependencyRule extends SystemEnumerationEntity<'PerformanceIndicatorValueDependencyRule', enumerations.PerformanceIndicatorValueDependencyRule> {}

export interface CorporateStructureLink {
    readonly ProductID: number | null;
    readonly GeoAreaID: number | null;
    readonly CustomerID: number | null;
    readonly TrackingLevel1ItemID: number | null;
    readonly TrackingLevel2ItemID: number | null;
    readonly TrackingLevel3ItemID: number | null;
    readonly TrackingLevel4ItemID: number | null;
    readonly CompanyLegalEntityID: number | null;
    readonly CompanyFunctionID: number | null;
}

export interface CorporateStructureFilter {
    readonly ProductIDs: (number | null)[];
    readonly GeoAreaIDs: (number | null)[];
    readonly CustomerIDs: (number | null)[];
    readonly TrackingLevel1ItemIDs: (number | null)[];
    readonly TrackingLevel2ItemIDs: (number | null)[];
    readonly TrackingLevel3ItemIDs: (number | null)[];
    readonly TrackingLevel4ItemIDs: (number | null)[];
    readonly CompanyLegalEntityIDs: (number | null)[];
    readonly CompanyFunctionIDs: (number | null)[];
}

export interface PerformanceManagementAlertType extends SystemEnumerationEntity<'PerformanceManagementAlertType', enumerations.PerformanceManagementAlertType> {}

export interface PerformanceManagementAlertActivityStatus extends SystemEnumerationEntity<'PerformanceManagementAlertActivityStatus', enumerations.PerformanceManagementAlertActivityStatus> { }

export interface PerformanceManagementAlertTriggerType extends SystemEnumerationEntity<'PerformanceManagementAlertTriggerType', enumerations.PerformanceManagementAlertTriggerType> { }

export interface PerformanceManagementAlertTriggerIndicator extends Entity<'PerformanceManagementAlertIndicator'> {
    readonly AlertID: number;
    readonly IndicatorID: number;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface PerformanceManagementAlertRecipient extends Entity<'PerformanceManagementAlertRecipient'> {
    readonly AlertID: number;
    readonly UserID: number;
}

export interface PerformanceManagementAlertIndicator extends SortableEntity<'PerformanceManagementAlertIndicator'> {
    readonly AlertID: number;
    readonly IndicatorID: number;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface PerformanceManagementAlertReviewSectionComment extends UserTrackingEntity<'PerformanceManagementAlertReviewSectionComment'> {
    readonly ReviewSectionID: number;
    readonly ScoreID: enumerations.PerformanceManagementAlertScore;
    readonly Comment: string;
}

export interface PerformanceManagementAlertReviewSectionReport extends SortableEntity<'PerformanceManagementAlertReviewSectionReport'> {
    readonly AlertID: number;
    readonly Text: string;
    readonly Comments: PerformanceManagementAlertReviewSectionComment[];
}

export interface PerformanceManagementAlertDashboardBlockComment extends UserTrackingEntity<'PerformanceManagementAlertDashboardBlockComment'> {
    readonly ReviewSectionID: number;
    readonly Comment: string;
}

export interface PerformanceManagementAlertDashboardBlockReport extends SortableEntity<'PerformanceManagementAlertDashboardBlockReport'> {
    readonly AlertID: number;
    readonly BlockID: number;
    readonly Comments: PerformanceManagementAlertDashboardBlockComment[];
}

export interface PerformanceManagementAlertScore extends SystemEnumerationEntity<'PerformanceManagementAlertScore', enumerations.PerformanceManagementAlertScore> {
    readonly Icon: string;
    readonly Color: string;
}

export interface PerformanceManagementAlertEmailTitleType extends SystemEnumerationEntity<'PerformanceManagementAlertEmailTitleType', enumerations.PerformanceManagementAlertEmailTitleType> { }

export interface PerformanceManagementAlertUserLoginURL extends Entity<'PerformanceManagementAlertUserLoginURL'> {
    readonly LoginURL: string;
}

export interface PerformanceManagementAlertReport extends UserTrackingEntity<'PerformanceManagementAlertReport'> {
    readonly Name: string;
    readonly TypeID: enumerations.PerformanceManagementAlertType;
    readonly ActivityStatusID: enumerations.PerformanceManagementAlertActivityStatus;
    readonly TriggerTypeID: enumerations.PerformanceManagementAlertTriggerType | null;
    readonly TriggerTimePeriodTypeID: enumerations.PeriodType | null;
    readonly TriggerIndicatorStartDateTypeID: enumerations.RelativeDateType | null;
    readonly TriggerIndicatorStartDateMonthOffset: number | null;
    readonly TriggerIndicatorNowDateTypeID: enumerations.RelativeDateType | null;
    readonly TriggerIndicatorNowDateMonthOffset: number | null;
    readonly TriggerIndicatorEndDateTypeID: enumerations.RelativeDateType | null;
    readonly TriggerIndicatorEndDateMonthOffset: number | null;
    readonly TriggerIndicatorBudgetUseLimit: number | null;
    readonly SendPreviewForAnalysis: boolean;
    readonly ResponsibleUserID: number | null;
    readonly AgendaFunctionID: number | null;
    readonly CoverLetterText: string;
    readonly RedirectToHomePage: boolean;
    readonly Updated: Date;
    readonly Recipients: PerformanceManagementAlertRecipient[];
    readonly TriggerIndicators: PerformanceManagementAlertTriggerIndicator[];
    readonly Indicators: PerformanceManagementAlertIndicator[];
    readonly Sections: PerformanceManagementAlertReviewSectionReport[];
    readonly Blocks: PerformanceManagementAlertDashboardBlockReport[];
    readonly LastSent: Date | null;
    readonly EvaluationVisible: boolean;
    readonly EmailTitleType: enumerations.PerformanceManagementAlertEmailTitleType;
    readonly CustomEmailTitle: string;
    readonly TemplateID: number | null;
    readonly SentToReview: Date | null;
    readonly SentToRecipients: Date | null;
    readonly CustomTargetPeriod: Date | null;
    readonly AgendaThemeID: number | null;
    readonly Custom: UserTrackingEntity<'PerformanceManagementAlertReport'>['Custom'] & {
        readonly preview: NetworkObject<void> & { UserID: number; };
        readonly evaluation: NetworkObject<void>;
        readonly userLoginURLs: PerformanceManagementAlertUserLoginURL[];
    };
}

export interface PerformanceIndicatorGroupReport extends SystemEnumerationEntity<'PerformanceIndicatorGroupReport', enumerations.PerformanceIndicatorGroup> {
    readonly IndicatorIDs: number[];
}

export interface PerformanceIndicatorFormula extends SortableEntity<'PerformanceIndicatorFormula'> {
    readonly TargetIndicatorID: number | null;
}

export interface PerformanceIndicatorReport extends Entity<'PerformanceIndicatorReport'> {
    readonly Identifier: string;
    readonly Name: string;
    readonly NameEN: string;
    readonly NameFI: string;
    readonly NameSE: string;
    readonly SystemTypeID: number;
    readonly DefaultPeriodTypeID: enumerations.PeriodType | null;
    readonly PerformancePerspectiveGroupID: number;
    readonly Depth: number | null;
    readonly Sort: number | null;
    readonly DimensionBudget: boolean;
    readonly DimensionForecast: boolean;
    readonly DimensionGoal: boolean;
    readonly TrackingLevels: enumerations.TrackingLevel[];
    readonly NegateFinancialValue: boolean;
    readonly SupportDrilling: boolean;
    readonly ValueSuffix: string;
    readonly MostDetailedPeriodTypeID: enumerations.PeriodType;
    readonly ShowInIndicatorMap: boolean;
    readonly Created: Date | null;
    readonly CreatorID: number | null;
    readonly FatherID: number | null;
    readonly HasAnyData: boolean;
    readonly UsedIn: number[];
    readonly Formula: PerformanceIndicatorFormula[];
    readonly IsFinancial: boolean;
    readonly FilteredIndicatorFormulaDynamicFilter: boolean;
}

export interface PerformanceIndicatorDataFilterRange {
    readonly StartDate?: Date;
    readonly EndDate?: Date;
}

export interface PerformanceIndicatorDataFilter extends PerformanceIndicatorDataFilterRange {
    readonly IndicatorID: number;
    readonly PeriodTypeID?: enumerations.PeriodType;
    readonly CorporateStructureLink: Partial<CorporateStructureLink>;
    readonly DimensionID?: enumerations.PerformanceIndicatorDimension;
}

export interface PerformanceIndicatorDataBase extends Entity<'PerformanceIndicatorDataBase'> {
    readonly PeriodTypeID: enumerations.PeriodType;
    readonly KeyPerformanceIndicatorID: number;
    readonly Date: Date;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly Actual: number;
    readonly Budget: number;
    readonly Forecast: number;
    readonly Goal: number;
    readonly ActualSourceID: number;
    readonly LastActualUpdate: Date;
    readonly BudgetSourceID: number;
    readonly ForecastSourceID: number;
    readonly GoalSourceID: number | null;
    readonly Comment: string;
}

export interface PerformanceIndicatorDataReport {
    readonly EntityType: 'PerformanceIndicatorDataReport';
    readonly ID: string;
    readonly IndicatorID: number;
    readonly Name: string;
    readonly ValueDependencyRuleID: number;
    readonly NegateFinancialValue: boolean;
    readonly ValueSuffix: string;
    readonly Data: PerformanceIndicatorDataBase[];
    readonly Custom: Entity<'PerformanceIndicatorDataReport'>['Custom'] & {
        readonly loadedRanges: {
            readonly start: Date | null;
            readonly end: Date | null;
            readonly status: enumerations.RequestStatus;
        }[];
    };
}

export interface PerformanceIndicatorAvailabilityReport extends Entity<'PerformanceIndicatorAvailabilityReport'> {
    readonly Identifier: string;
    readonly Name: string;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly ActualPeriodTypes: number[];
    readonly BudgetPeriodTypes: number[];
    readonly ForecastPeriodTypes: number[];
    readonly GoalPeriodTypes: number[];
}

export interface Country extends Entity<'Country'> {
    readonly Name: string;
}

export interface StakeholderNewsWatchFilterLevel extends SystemEnumerationEntity<'StakeholderNewsWatchFilterLevel', enumerations.StakeholderNewsWatchFilterLevel> { }

export interface MarketScopeType extends SystemEnumerationEntity<'MarketScopeType', enumerations.MarketScopeType> { }

export interface NewsEntryAutomatedSource extends Entity<'NewsEntryAutomatedSource'> {
    readonly SourceName: string;
    readonly ShortSourceName: string;
}

export interface StakeholderNewsEntryAutomatedSource extends Entity<'StakeholderNewsEntryAutomatedSource'> {
    readonly SourceID: number;
    readonly StakeholderID: number;
    readonly CompanyFunctionID: number | null;
    readonly LastPollDate: Date;
    readonly IsActive: boolean;
}

export interface StakeholderComment extends UserTrackingEntity<'StakeholderComment'> {
    readonly StakeholderID: number;
    readonly Comment: string;
    readonly ReplyToCommentID: number | null;
}

export interface StakeholderReport extends NestableEntity<'StakeholderReport'>, UserTrackingEntity<'StakeholderReport'> {
    readonly TypeID: number;
    readonly Name: string;
    readonly Description: string;
    readonly CountryID: number;
    readonly BusinessID: string;
    readonly HumanReadableBusinessID: string;
    readonly DomainURI: string;
    readonly Importance: number | null;
    readonly MarketID: number;
    readonly Comments: NetworkPaginationSlice<StakeholderComment>;
    readonly NewsWatchOn: boolean;
    readonly WebWatchOn: boolean;
    readonly BrandNames: string;
    readonly NewsModeratorID: number | null;
    readonly NewsWatchFilterLevelID: enumerations.StakeholderNewsWatchFilterLevel | null;
    readonly MarketScopeTypeID: enumerations.MarketScopeType | null;
    readonly MarketScopeEntityID: enumerations.MarketScopeType | null;
}

export interface StakeholderTypeReport extends Entity<'StakeholderTypeReport'> {
    readonly Description: string;
    readonly Stakeholders: NetworkPaginationSlice<StakeholderReport>;
}

export interface StakeholderMarketPositioningMapViewPosition extends UserTrackingEntity<'StakeholderMarketPositioningMapViewPosition'> {
    readonly ViewID: number;
    readonly StakeholderID: number | null;
    readonly X: number;
    readonly Y: number;
    readonly ViewItemID: number | null;
}

export interface StakeholderMarketPositioningMapViewCustomItem extends UserTrackingEntity<'StakeholderMarketPositioningMapViewCustomItem'> {
    readonly ViewID: number;
    readonly Name: string;
}

export interface StakeholderMarketPositioningMapViewReport extends UserTrackingEntity<'StakeholderMarketPositioningMapViewReport'> {
    readonly GroupID: number | null;
    readonly Name: string;
    readonly XMinText: string;
    readonly XMaxText: string;
    readonly YMinText: string;
    readonly YMaxText: string;
    readonly Description: string;
    readonly ShowCurrentCompany: boolean;
    readonly TypeID: number | null;
    readonly Positions: NetworkPaginationSlice<StakeholderMarketPositioningMapViewPosition>;
    readonly Items: NetworkPaginationSlice<StakeholderMarketPositioningMapViewCustomItem>;
    readonly Custom: Entity<'StakeholderMarketPositioningMapViewReport'>['Custom'] & {
        readonly invitationStatus: enumerations.RequestStatus;
    };
}

export interface StakeholderMarketPositioningMapViewTemplate extends Entity<'StakeholderMarketPositioningMapViewTemplate'> {
    readonly Name: string;
    readonly Description: string;
    readonly ThumbnailURL: string;
    readonly ScreenshotURL: string;
    readonly View: StakeholderMarketPositioningMapViewReport;
}


export interface StakeholderBenchmarkDimensionValue extends UserTrackingEntity<'StakeholderBenchmarkDimensionValue'> {
    readonly DimensionID: number;
    readonly StakeholderID: number | null;
    readonly Value: number;
    readonly BenchmarkItemID: number | null;
}

export interface StakeholderBenchmarkDimensionReport extends SortableEntity<'StakeholderBenchmarkDimensionReport'>, UserTrackingEntity<'StakeholderBenchmarkDimensionReport'> {
    readonly BenchmarkID: number;
    readonly Name: string;
    readonly Values: NetworkPaginationSlice<StakeholderBenchmarkDimensionValue>;
}

export interface StakeholderBenchmarkCustomItem extends UserTrackingEntity<'StakeholderBenchmarkCustomItem'> {
    readonly BenchmarkID: number;
    readonly Name: string;
}

export interface StakeholderBenchmarkReport extends UserTrackingEntity<'StakeholderBenchmarkReport'> {
    readonly GroupID: number | null;
    readonly Name: string;
    readonly MinText: string;
    readonly MaxText: string;
    readonly Description: string;
    readonly ShowCurrentCompany: boolean;
    readonly TypeID: number | null;
    readonly Dimensions: NetworkPaginationSlice<StakeholderBenchmarkDimensionReport>;
    readonly Items: NetworkPaginationSlice<StakeholderBenchmarkCustomItem>;
    readonly Custom: Entity<'StakeholderBenchmarkReport'>['Custom'] & {
        readonly invitationStatus: enumerations.RequestStatus;
    };
}

export interface StakeholderBenchmarkTemplate extends Entity<'StakeholderBenchmarkTemplate'> {
    readonly Name: string;
    readonly Description: string;
    readonly ThumbnailURL: string;
    readonly ScreenshotURL: string;
    readonly Benchmark: StakeholderBenchmarkReport;
}


export interface FinancialFiscalReport {
    readonly FiscalPeriodStart: Date;
    readonly FiscalPeriodEnd: Date;
    readonly Revenue: number | null;
    readonly FiscalResult: number | null;
}

export interface FinancialsAndDecisionMakers extends Entity<'FinancialsAndDecisionMakers'> {
    readonly FiscalReports: FinancialFiscalReport[];
    readonly ResponsiblePersons: Entity[];
}

export interface CorporateStructureSortMode extends SystemEnumerationEntity<'CorporateStructureSortMode', enumerations.CorporateStructureSortMode> { }

export interface TrackingLevel extends SystemEnumerationEntity<'TrackingLevel', enumerations.TrackingLevel> {
    readonly DefaultDescription: string;
    readonly DescriptionFI: string;
    readonly DescriptionEN: string;
    readonly DescriptionSE: string;
    readonly IsCustom: boolean;
    readonly IsEnabled: boolean;
    readonly IsActive: boolean;
    readonly DefaultDashboardPageID: number | null;
}

export interface TrackingLevelItemBase extends Entity<'TrackingLevelItemBase'> {
    readonly Name: string;
    readonly ExternalIdentifier: string;
    readonly IsDefault: boolean;
    readonly IsActive: boolean;
}

export interface Product extends TrackingLevelItemBase { }
export interface GeoArea extends TrackingLevelItemBase { }
export interface Customer extends TrackingLevelItemBase { }
export interface TrackingLevel1Item extends TrackingLevelItemBase { }
export interface TrackingLevel2Item extends TrackingLevelItemBase { }
export interface TrackingLevel3Item extends TrackingLevelItemBase { }
export interface TrackingLevel4Item extends TrackingLevelItemBase { }
export interface CompanyLegalEntity extends TrackingLevelItemBase {
    readonly EntityTypeID: enumerations.CompanyLegalEntityType;
    readonly FatherID: number | null;
    readonly OrganizationIdentifier: string;
    readonly FinancialIntegrationSourceID: enumerations.IntegrationSource | null;
    readonly Custom: TrackingLevelItemBase['Custom'] & {
        readonly pendingCalculationSources?: enumerations.IntegrationSource[];
    };
}
export interface CompanyFunction extends TrackingLevelItemBase {
    readonly ExternalID: string;
    readonly FatherID: number | null;
    readonly CompanyLegalEntityID: number | null;
    readonly TeamDashboardBlock1ID: number | null;
    readonly TeamDashboardBlock2ID: number | null;
    readonly Color: string;
    readonly Custom: TrackingLevelItemBase['Custom'] & {
        roadmapStartDate: Date;
        roadmapEndDate: Date;
        journeyStartDate: Date | null;
        journeyEndDate: Date | null;
    };
}

export interface CompanyFunctionNetworkArray extends NetworkArray<CompanyFunction> {
    readonly Custom: NetworkArray<CompanyFunction>['Custom'] & {
        roadmapStartDate: Date;
        roadmapEndDate: Date;
    };
}

export interface ERPSyncItem extends Entity<'ERPSyncItem'> {
    readonly ExternalID: string;
    readonly Name: string;
    readonly Code: string;
    readonly ParentGroupName: string;
    readonly ParentGroupID: number | null;
    readonly IsUserOverride: boolean;
    readonly CompanyLegalEntityID: number | null;
    readonly IsActive: boolean;
    readonly DetailsSynced: boolean;
}


export interface ERPSyncItem extends Entity<'ERPSyncItem'> {
    readonly ExternalID: string;
    readonly Name: string;
    readonly Code: string;
    readonly ParentGroupName: string;
    readonly ParentGroupID: number | null;
    readonly IsUserOverride: boolean;
    readonly CompanyLegalEntityID: number | null;
    readonly IsActive: boolean;
    readonly DetailsSynced: boolean;
}

export interface ERPSyncItemMonthData extends Entity<'ERPSyncItemMonthData'> {
    readonly ERPSyncProductID: number;
    readonly IndicatorID: number;
    readonly SelectedYear: number;
    readonly SelectedMonth: number;
    readonly Actual: number;
    readonly LastUpdate: Date;
}

export interface ERPSyncProductReport extends ERPSyncItem {
    readonly Description: string;
    readonly UnitPrice: number;
    readonly InventoryAmount: number;
    readonly Data: ERPSyncItemMonthData[][];
}

export interface ERPSyncCustomerReport extends ERPSyncItem {
    readonly OrganizationIdentifier: string;
    readonly NameExtension: string;
    readonly AddressLine: string;
    readonly AddressLineExtension: string;
    readonly PostalCode: string;
    readonly City: string;
    readonly CountryCodeISO3166: string;
    readonly LocationLatitude: number | null;
    readonly LocationLongitude: number | null;
    readonly RegionID: number | null;
    readonly GeoDecodeStatusID: number | null;
    readonly IndustryID: number | null;
    readonly Data: ERPSyncItemMonthData[][];
}

export interface ERPSyncItemPaginationSlice<T extends ERPSyncItem> extends NetworkPaginationSlice<T> {
    ParentGroupID: number | null;
    IsActive: boolean | null;
    ActiveCount: number;
}

export interface NewsEntrySettingsBase {
    readonly Name: string;
    readonly ModeratorID: number;
    readonly SWOTTopicID: number | null;
    readonly PersonalTopicID: number | null;
    readonly TrendTopicID: number | null;
    readonly StakeholderID: number | null;
    readonly CompanyLegalEntityID: number | null;
    readonly CompanyFunctionID: number | null;
    readonly PESTELAreaID: number | null;
}

export interface NewsEntryFilter {
    readonly CategoryIDs: number[];
    readonly SourceIDs: (number | null)[];
    readonly SourceCategoryIDs: number[];
    readonly SourceStakeholderTypeIDs: (number | null)[];
    readonly EntryStatusIDs: (number | null)[];
    readonly UserStatusIDs: (number | null)[];
    readonly Text: string;
    readonly PESTELAreaIDs: (number | null)[];
    readonly SWOTTopicIDs: (number | null)[];
    readonly PersonalTopicIDs: (number | null)[];
    readonly TrendTopicIDs: (number | null)[];
    readonly ModeratorIDs: number[];
    readonly ModeratorFunctionIDs: number[];
    readonly StakeholderIDs: (number | null)[];
    readonly CompanyLegalEntityIDs: (number | null)[];
    readonly CompanyFunctionIDs: (number | null)[];
    readonly AddedByUserIDs: (number | null)[];
    readonly ApprovedByUserIDs: (number | null)[];
    readonly PreferredOnly: boolean;
    readonly Period: number;
    readonly CustomPeriodStart: Date | null;
    readonly CustomPeriodEnd: Date | null;
    readonly InsightLinkBusinessModelAreaIDs: (number | null)[];
    readonly InsightLinkTrendTopicIDs: (number | null)[];
    readonly InsightLinkStakeholderIDs: (number | null)[];
}

export interface NewsEntryComment {
    readonly NewsID: number;
    readonly DateAdded: Date;
    readonly UserID: number;
    readonly Comment: string;
}

export interface FinancialIndicator extends Entity<'FinancialIndicator'> {
    readonly Identifier: string;
    readonly Name: string;
}

export interface FinancialIndicatorDataFilterRange {
    readonly StartYear?: number;
    readonly EndYear?: number;
}

export interface FinancialIndicatorDataFilter extends FinancialIndicatorDataFilterRange {
    readonly IndicatorID?: number;
    readonly MarketScopeTypeID?: enumerations.MarketScopeType;
    readonly BusinessID?: string;
}

export interface FinancialIndicatorDataBase extends Entity<'FinancialIndicatorDataBase'> {
    readonly Year: number;
    readonly Value: number;
}

export interface FinancialIndicatorDataReport {
    readonly ID: string;
    readonly IndicatorID: number;
    readonly MarketScopeTypeID: enumerations.MarketScopeType;
    readonly BusinessID: string;
    readonly StartYear: number | null;
    readonly EndYear: number | null;
    readonly Name: string;
    readonly Data: FinancialIndicatorDataBase[];
    readonly Custom: Entity<'FinancialIndicatorDataReport'>['Custom'] & {
        readonly loadedRanges: {
            readonly start: number | null;
            readonly end: number | null;
            readonly status: enumerations.RequestStatus;
        }[];
    };
}

export interface ForesightScenarioReport extends UserTrackingEntity<'ForesightScenarioReport'> {
    readonly Name: string;
    readonly Description: string;
    readonly ChildrenTrackingLevelID: enumerations.TrackingLevel | null;
    readonly CashflowReceivablesAmount: number;
    readonly CashflowPayablesAmount: number;
    readonly CashflowPastIndicatorID: number;
    readonly CashflowFutureIndicatorID: number;
    readonly SalesPastIndicatorID: number;
    readonly SalesFutureIndicatorID: number;
    readonly MaterialCostsPastIndicatorID: number;
    readonly MaterialCostsFutureIndicatorID: number;
    readonly TotalCostsPastIndicatorID: number;
    readonly TotalCostsFutureIndicatorID: number;
    readonly MarketGrowth: number;
    readonly IsDefault: boolean;
    readonly SimulateVAT: boolean;
    readonly CashflowForecastInFromOperationsIndicatorID: number;
    readonly CashflowForecastOutFromOperationsIndicatorID: number;
    readonly CashflowCutOffOverridenDate: Date | null ;
    readonly CashflowForecastInvestmentsIndicatorID: number;
    readonly CashflowForecastFinancialItemsIndicatorID: number;
    readonly SalesMarginIndicatorID: number;
    readonly GrossMarginIndicatorID: number;
    readonly NotifyAutomatically: boolean;
    readonly NotificationCoverLetter: string;
    readonly ElementTypes: ForesightScenarioElementType[];
    readonly Levels: ForesightScenarioLevelReport[];
    readonly AccountReceivablesDistributions: BusinessSimulatorCashflowAccountReceivablesDistribution[];
    readonly SalesReceivablesDistributions: BusinessSimulatorCashflowSalesReceivablesDistribution[];
    readonly CapitalItemValues: NetworkPaginationSlice<BusinessSimulatorCashflowCapitalItemValue>;
    readonly NotificationRecipients: ForesightScenarioNotificationRecipient[];
    readonly Custom: UserTrackingEntity<'ForesightScenarioReport'>['Custom'] & {
        readonly subdivisionItemIDs: number[];
        readonly export: FileTransferHelperEntity;
        readonly calculation: NetworkSynchronizable;
    };
}

export interface ForesightScenarioLevelReport extends Entity<'ForesightScenarioLevelReport'> {
    readonly ScenarioID: number;
    readonly AggregationStartDate: Date | null;
    readonly AggregationEndDate: Date | null;
    readonly IsActive: boolean;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly Elements: ForesightScenarioElementReport[];
}

export interface BusinessSimulatorCashflowAccountReceivablesDistribution extends Entity<'BusinessSimulatorCashflowAccountReceivablesDistribution'> {
    readonly ScenarioID: number;
    readonly MonthOffset: number;
    readonly Value: number;
}

export interface BusinessSimulatorCashflowSalesReceivablesDistribution extends Entity<'BusinessSimulatorCashflowSalesReceivablesDistribution'> {
    readonly ScenarioID: number;
    readonly MonthOffset: number;
    readonly Value: number;
}

export interface BusinessSimulatorCashflowCapitalItemGroup extends SystemEnumerationEntity<'BusinessSimulatorCashflowCapitalItemGroup', enumerations.CashflowCapitalItemGroup> {}

export interface BusinessSimulatorCashflowCapitalItem extends SystemEnumerationEntity<'BusinessSimulatorCashflowCapitalItem', enumerations.CashflowCapitalItem> {
    readonly GroupID: enumerations.CashflowCapitalItemGroup;
}

export interface BusinessSimulatorCashflowCapitalItemValue extends Entity<'BusinessSimulatorCashflowCapitalItemValue'> {
    readonly ScenarioID: number;
    readonly ItemID: enumerations.CashflowCapitalItem;
    readonly Date: Date;
    readonly Value: number;
}

export interface ForesightScenarioNotificationRecipient extends Entity<'ForesightScenarioNotificationRecipient'> {
    readonly ScenarioID: number;
    readonly UserID: number;
}

export interface ForesightScenarioElementType extends SortableEntity<'ForesightScenarioElementType'> {
    readonly ScenarioID: number;
    readonly IndicatorID: number;
    readonly BalanceTargetIndicatorID: number | null;
    readonly CashflowCalculationTypeID: enumerations.ForesightScenarioElementCashflowCalculationType;
    readonly AffectsCashflow: boolean;
    readonly IncludedInSalesMargin: boolean;
}

export interface ForesightScenarioBaselineCalculation extends SystemEnumerationEntity<'ForesightScenarioBaselineCalculation', enumerations.ForesightScenarioBaselineCalculation> {
    readonly Name: string;
    readonly Description: string;
    readonly BaseIndicatorID: number | null;
}

export interface ForesightScenarioElementReport extends UserTrackingEntity<'ForesightScenarioElementReport'> {
    readonly LevelID: number;
    readonly TypeID: number;
    readonly BaselineCalculationID: enumerations.ForesightScenarioBaselineCalculation;
    readonly ResponsibleUserID: number;
    readonly Name: string;
    readonly PastIndicatorID: number;
    readonly FutureIndicatorID: number;
    readonly CalculatePeriodsAfter: Date | null;
    readonly CalculatePeriodsBefore: Date | null;
    readonly ManualShare: number;
    readonly Factors: ForesightScenarioFactor[];
}

export interface ForesightScenarioFactor extends SortableEntity<'ForesightScenarioFactor'> {
    readonly Created: Date;
    readonly CreatorID: number;
    readonly ElementID: number;
    readonly Name: string;
    readonly Impact: number;
    readonly Relative: boolean;
    readonly Smooth: boolean;
    readonly StartDate: Date | null;
    readonly ImpactDate: Date;
    readonly EndDate: Date | null;
    readonly FinalOnly: boolean;
    readonly Enabled: boolean;
    readonly BreakthroughID: number | null;
}

export interface GoalMetricValueSource extends SystemEnumerationEntity<'GoalMetricValueSource', enumerations.GoalMetricValueSource> {}
export interface GoalMetricValueType extends SystemEnumerationEntity<'GoalMetricValueType', enumerations.GoalMetricValueType> { }

export interface GoalMetricReport extends UserTrackingEntity<'GoalMetricReport'> {
    readonly GoalID: number | null;
    readonly ProgramID: number | null;
    readonly Name: string;
    readonly IndicatorID: number | null;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly PeriodTypeID: enumerations.PeriodType;
    readonly StartValue: number | null;
    readonly MilestoneID: number | null;
    readonly GoalValue: number | null;
    readonly GoalValueTypeID: enumerations.GoalMetricValueType;
    readonly InputManually: boolean;
    readonly SheetGoogleConfigurationID: number | null;
    readonly StrategyID: number | null;
    readonly ValueSourceID: enumerations.GoalMetricValueSource;
    readonly FatherID: number | null;
    readonly AggregateGoal: boolean;
    readonly UseIncreasingDirection: boolean;
    readonly ValueDependencyRuleID: enumerations.PerformanceIndicatorValueDependencyRule | null;
    readonly Data: PerformanceIndicatorDataReport[] | [];
    readonly PerformancePerspectiveGroupID: enumerations.PerformancePerspectiveGroup | null;
    readonly Expired: boolean;
    readonly CanUserEditMetric: boolean;

    readonly Custom: Entity<'GoalMetricReport'>['Custom'] & {
        readonly isProgramMetric: boolean;
        readonly setInitialValueFromActuals: boolean;
        readonly initialValue: number | null;
    };
}

export interface StrategyProgressCalculationMethod extends SystemEnumerationEntity<'StrategyProgressCalculationMethod', enumerations.StrategyProgressCalculationMethod> {
    readonly Hint: string;
}
export interface StrategyProgressCalculationSource extends SystemEnumerationEntity<'StrategyProgressCalculationSource', enumerations.StrategyProgressCalculationSource> {}
export interface StrategyExecutionSecondaryStatusUpdateType extends SystemEnumerationEntity<'StrategyExecutionSecondaryStatusUpdateType', enumerations.StrategyExecutionSecondaryStatusUpdateType> {}

export interface StrategicProgramStatus extends SystemEnumerationEntity<'StrategicProgramStatus', enumerations.StrategicProgramStatus> { }
export interface StrategicProgramFinalStatus extends SystemEnumerationEntity<'StrategicProgramFinalStatus', enumerations.StrategicProgramFinalStatus> {
    readonly Icon: string;
}

export interface StrategicProgramUpdateFrequency extends SystemEnumerationEntity<'StrategicProgramUpdateFrequency', enumerations.StrategicProgramUpdateFrequency> { }
export interface StrategicProgramRiskLevel extends SystemEnumerationEntity<'StrategicProgramRiskLevel', enumerations.StrategicProgramRiskLevel> {
    readonly Icon: string;
}

export interface StrategicProgramStatusUpdateReport extends Entity<'StrategicProgramStatusUpdateReport'> {
    readonly ProgramID: number;
    readonly UserID: number;
    readonly UpdateDate: Date;
    readonly RiskLevelID: enumerations.StrategicProgramRiskLevel;
    readonly NewValue: number;
    readonly Comment: string;
    readonly NotificationRecipients: NetworkPaginationSlice<StrategicProgramStatusUpdateRecipient>;

    readonly CanUserEditStatusUpdate: boolean;
}

export interface StrategicProgramStatusUpdateRecipient extends Entity<'StrategicProgramStatusUpdateRecipient'> {
    readonly StatusUpdateID: number;
    readonly UserID: number;
    readonly SentDate: Date;
}

export interface StrategicProgramComment extends Entity<'StrategicProgramComment'> {
    readonly ProgramID: number;
    readonly UserID: number;
    readonly DateAdded: Date;
    readonly Comment: string;
    readonly ReplyToCommentID: number | null;
}

export interface StrategicProgramSecondaryOwner extends Entity<'StrategicProgramSecondaryOwner'> {
    readonly ProgramID: number;
    readonly UserID: number;
}

export interface StrategicProgramReport extends Entity<'StrategicProgramReport'> {
    readonly Name: string;
    readonly Description: string;
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly FunctionID: number | null;
    readonly OwnerUserID: number;
    readonly FatherID: number | null;
    readonly StrategySelectionID: number | null;
    readonly StatusID: enumerations.StrategicProgramStatus;
    readonly RoadmapEnabled: boolean;
    readonly RoadmapStartDate: Date | null;
    readonly RoadmapEndDate: Date | null;
    readonly UpdateFrequencyID: enumerations.StrategicProgramUpdateFrequency;
    readonly LastUpdateDate: Date;
    readonly LastUpdateNotificationSentDate: Date | null;
    readonly ParentMilestoneID: number;
    readonly CustomLabel: string;
    readonly Progress: number;
    readonly RiskLevelID: enumerations.StrategicProgramRiskLevel | null;
    readonly NextUpdateNotificationSendDate: Date | null;
    readonly DateClosed: Date | null;
    readonly FinalStatusID: enumerations.StrategicProgramFinalStatus;
    readonly FinalStatusComment: string;
    readonly SendStatusUpdateNotifications: boolean;
    readonly CompanyFunctionID: number | null;
    readonly NoProgressTracking: boolean;
    readonly ProgressCalculationMethodID: enumerations.StrategyProgressCalculationMethod;
    readonly ProgressCalculationSourceID: enumerations.StrategyProgressCalculationSource | null;
    readonly ConnectedToMilestoneID: number | null;
    readonly ConnectedToProgramID: number | null;
    readonly ConnectableAggregationTarget: boolean;
    readonly SecondaryStatusUpdateTypeID: enumerations.StrategyExecutionSecondaryStatusUpdateType;

    readonly Metric: GoalMetricReport | null;
    readonly StatusUpdates: NetworkPaginationSlice<StrategicProgramStatusUpdateReport>;
    readonly Comments: NetworkPaginationSlice<StrategicProgramComment>;
    readonly CustomFieldLinks: StrategyExecutionCustomFieldLink[];
    readonly CustomEntityData: StrategyExecutionCustomEntityData[];
    readonly SecondaryOwners: StrategicProgramSecondaryOwner[];

    readonly ProgressUpdatesAvailable: boolean;

    readonly CanUserEditProgram: boolean;
    readonly CanUserAddFreeNode: boolean;
    readonly CanUserEditMetrics: boolean;
    readonly CanUserMakeStatusUpdate: boolean;
    readonly CanUserActivateProgram: boolean;
    readonly CanUserCloseProgram: boolean;
    readonly CanUserEditDescriptions: boolean;
    readonly CanUserAddSubProgram: boolean;
    readonly CanUserAddMilestone: boolean;

    readonly Custom: Entity<'StrategicProgramReport'>['Custom'] & {
        isMetricSaving?: boolean;
    };
}

export interface StrategicGoalMilestoneComment extends Entity<'StrategicGoalMilestoneComment'> {
    readonly MilestoneID: number;
    readonly UserID: number;
    readonly DateAdded: Date;
    readonly Comment: string;
    readonly ReplyToCommentID: number | null;
}

interface StrategicGoalMilestoneStatusUpdateBase<ET extends string = 'StrategicGoalMilestoneStatusUpdateBase'> extends UserTrackingEntity<ET> {
    readonly MilestoneID: number;
    readonly AchievabilityID: enumerations.StrategicGoalMilestoneAchievability;
    readonly Comment: string;
    readonly UpdateDate: Date;
}

export interface StrategicGoalMilestoneStatusUpdate extends StrategicGoalMilestoneStatusUpdateBase<'StrategicGoalMilestoneStatusUpdate'> {}
export interface StrategicGoalMilestoneSecondaryStatusUpdate extends StrategicGoalMilestoneStatusUpdateBase<'StrategicGoalMilestoneSecondaryStatusUpdate'> {}

export interface StrategicGoalCriticalSuccessFactorReport extends UserTrackingEntity<'StrategicGoalCriticalSuccessFactorReport'> {
    readonly GoalID: number;
    readonly Factor: string;

    readonly CanUserEditCriticalSuccessFactor: boolean;
}

export interface StrategicGoalMilestoneReport extends UserTrackingEntity<'StrategicGoalMilestoneReport'> {
    readonly Name: string;
    readonly AchievedByDate: Date;
    readonly GoalID: number | null;
    readonly Description: string;
    readonly OwnerUserID: number | null;
    readonly StatusID: enumerations.StrategicGoalMilestoneStatus;
    readonly AgendaTeamID: number | null;
    readonly AchievabilityID: enumerations.StrategicGoalMilestoneAchievability | null;
    readonly ReasonOfClosing: string;
    readonly StartDate: Date;
    readonly ArchivedDate: Date | null;
    readonly FinalStatusID: enumerations.StrategicGoalMilestoneFinalStatus;
    readonly SuccessCalculationCurrentPercentage: number | null;
    readonly SuccessCalculationStatusUpdateID: enumerations.StrategicGoalMilestoneFinalStatus;
    readonly AskResponsiblePersonToChangeThePlan: boolean;
    readonly CustomLabel: string;
    readonly Sort: number;
    readonly CompanyFunctionID: number | null;
    readonly CompanyVirtualTeamID: number | null;
    readonly FatherID: number | null;
    readonly ParentProgramID: number | null;
    readonly CompletedDate: Date | null;
    readonly UpdatedBy: number | null;
    readonly ProgressCalculationMethodID: enumerations.StrategyProgressCalculationMethod;
    readonly ProgressCalculationSourceID: enumerations.StrategyProgressCalculationSource | null;
    readonly ConnectedToMilestoneID: number | null;
    readonly ConnectedToProgramID: number | null;
    readonly ConnectableAggregationTarget: boolean;
    readonly SecondaryStatusUpdateTypeID: enumerations.StrategyExecutionSecondaryStatusUpdateType;
    readonly PerformancePerspectiveGroupID: enumerations.PerformancePerspectiveGroup | null;

    readonly Comments: NetworkPaginationSlice<StrategicGoalMilestoneComment>;
    readonly Metrics: NetworkPaginationSlice<GoalMetricReport>;
    readonly StatusUpdates: NetworkPaginationSlice<StrategicGoalMilestoneStatusUpdate>;
    readonly SecondaryStatusUpdates: NetworkPaginationSlice<StrategicGoalMilestoneSecondaryStatusUpdate>;
    readonly OriginalStartDate: Date | null;
    readonly OriginalAchievedByDate: Date | null;
    readonly CustomFieldLinks: StrategyExecutionCustomFieldLink[];
    readonly CustomEntityData: StrategyExecutionCustomEntityData[];
    readonly TeamLinks: StrategicGoalMilestoneTeamLink[];
    readonly SecondaryOwners: StrategicGoalMilestoneSecondaryOwner[];

    readonly ProgressUpdatesAvailable: boolean;

    readonly CanUserAddMilestone: boolean;
    readonly CanUserEditMilestone: boolean;
    readonly CanUserPublishMilestone: boolean;
    readonly CanUserAddFreeNode: boolean;
    readonly CanUserMakeStatusUpdate: boolean;
    readonly CanUserMakeSecondaryStatusUpdate: boolean;
    readonly CanUserEditMetrics: boolean;
    readonly CanUserAddProgram: boolean;
    readonly CanUserAddActiveProgram: boolean;
    readonly CanUserAddInitiative: boolean;
    readonly CanUserEditDescriptions: boolean;

    readonly ArtificialGoalID: number | null;
    readonly Expired: boolean;

    readonly Custom: Entity<'StrategicGoalMilestoneReport'>['Custom'] & {
        strategyNamingTypeID?: enumerations.StrategyNamingType;
    };
}

export interface StrategicGoalReport extends Entity<'StrategicGoalReport'> {
    readonly Name: string;
    readonly StrategyDescriptionID: number | null;
    readonly PerformancePerspectiveID: number | null;
    readonly IdealState: string;
    readonly CreatedDate: Date;
    readonly CreatedBy: number;
    readonly CustomLabel: string;
    readonly CompanyFunctionID: number | null;
    readonly CompanyVirtualTeamID: number | null;
    readonly OwnerID: number | null;
    readonly StartDate: Date | null;
    readonly EndDate: Date | null;
    readonly ExcludedFromBusinessReview: boolean;

    readonly Metrics: GoalMetricReport[];
    readonly CriticalSuccessFactors: StrategicGoalCriticalSuccessFactorReport[];

    readonly CanUserEditGoal: boolean;
    readonly CanUserAddFreeNode: boolean;
    readonly CanUserAddMilestone: boolean;
    readonly CanUserEditCriticalSuccessFactor: boolean;
    readonly CanUserEditMetrics: boolean;
    readonly CanUserEditDescriptions: boolean;
    readonly CanUserExcludeGoalFromBusinessReview: boolean;

    readonly IsArtificial: boolean;
    readonly ArtificialStrategyDescriptionID: number | null;

    readonly Custom: Entity<'StrategicGoalReport'>['Custom'] & {
        createdInRoadmap?: boolean;
        journeyStartDate: Date | null;
        journeyEndDate: Date | null;
        roadmapStartDates: { [key: number]: Date | undefined; }; // key should be a company function's ID or 0 (for goal level setting)
        isMetricSaving?: boolean;
    };
}

export interface StrategyTaxonomy extends SystemEnumerationEntity<'StrategyTaxonomy', enumerations.StrategyTaxonomy> {
    readonly DefaultDescription: string;
    readonly DescriptionOverride: string;
    readonly IsActive: boolean;
}

export interface StrategyExecutionCustomFieldType extends SystemEnumerationEntity<'StrategyExecutionCustomFieldType', enumerations.StrategyExecutionCustomFieldType> { }

export interface StrategyExecutionCustomFieldLink extends Entity<'StrategyExecutionCustomFieldLink'> {
    readonly CustomFieldID: number;
    readonly GoalMilestoneID: number | null;
    readonly StrategicProgramID: number | null;
    readonly CustomFieldOptionID: number | null;
    readonly CustomFieldTextValue: string;
    readonly CustomFieldDateValue: Date | null;
}

export interface StrategicGoalMilestoneTeamLink extends Entity<'StrategicGoalMilestoneTeamLink'> {
    readonly GoalMilestoneID: number;
    readonly CompanyFunctionID: number | null;
    readonly VirtualTeamID: number | null;
}

export interface StrategicGoalMilestoneSecondaryOwner extends Entity<'StrategicGoalMilestoneSecondaryOwner'> {
    readonly MilestoneID: number;
    readonly UserID: number;
}

export interface StrategyExecutionCustomFieldOption extends Entity<'StrategyExecutionCustomFieldOption'> {
    readonly CustomFieldID: number;
    readonly OptionText: string;
    readonly Sort: number;
    readonly FatherID: number | null;
}

export interface StrategyExecutionCustomFieldReport extends Entity<'StrategyExecutionCustomFieldReport'> {
    readonly Name: string;
    readonly IsGoalMilestoneField: boolean;
    readonly AskInStatusUpdate: boolean;
    readonly ShowInRoadmapRange: boolean;
    readonly IsStrategicProgramField: boolean;
    readonly FieldTypeID: enumerations.StrategyExecutionCustomFieldType;
    readonly Options: StrategyExecutionCustomFieldOption[];
}

export interface StrategyExecutionCustomEntity extends Entity<'StrategyExecutionCustomEntity'> {
    readonly Name: string;
    readonly IsMilestoneEntity: boolean;
    readonly IsProgramEntity: boolean;
    readonly StatusUpdateActive: boolean;
    readonly StatusUpdateQuestion: string;
}

export interface StrategyExecutionCustomEntityData extends UserTrackingEntity<'StrategyExecutionCustomEntityData'> {
    readonly CustomEntityID: number;
    readonly MilestoneID: number | null;
    readonly ProgramID: number | null;
    readonly Contents: string;
}

export interface StrategyExportItemSettings extends Entity<'StrategyExportItemSettings'> {
    readonly PublishTargetEntityTypeID: enumerations.ExportStrategyTargetEntityType;
    readonly PublishTargetEntityID: number;
    readonly EntityID: number;
    readonly EntityParentID: number | null;
    readonly EntityTypeID: enumerations.StrategyElementType;
    readonly Name: string;
    readonly Description: string;
    readonly ResponsibleUserID: number | null;
    readonly CompanyFunctionID: number | null;
    readonly CompanyVirtualTeamID: number | null;
    readonly StartDate: Date | null;
    readonly AchievedByDate: Date | null;
    readonly ProgressCalculationMethodID: enumerations.StrategyProgressCalculationMethod | null;
    readonly ProgressCalculationSourceID: enumerations.StrategyProgressCalculationSource | null;
    readonly MetricName: string;
    readonly TargetValue: number | null;
    readonly ForcePublish: boolean;
    readonly SendNotification: boolean;
    readonly CategoryID: number | null;
    readonly CategoryName: string;
}

export interface BusinessReviewSectionType extends SystemEnumerationEntity<'BusinessReviewSectionType', enumerations.BusinessReviewSectionType> { }
export interface BusinessReviewViewType extends SystemEnumerationEntity<'BusinessReviewViewType', enumerations.BusinessReviewViewType> { }

export interface BusinessReviewExcludedSection extends Entity<'BusinessReviewExcludedSection'> {
    readonly UserID: number | null;
    readonly SectionTypeID: enumerations.BusinessReviewSectionType;
    readonly Excluded: boolean;
}

export interface BusinessReviewUserSettings extends Entity<'BusinessReviewUserSettings'> {
    readonly UserID: number | null;
    readonly ShowStrategy: boolean;
    readonly ShowGoals: boolean;
    readonly ShowGoalsMetrics: boolean;
    readonly ShowBreakthrough: boolean;
    readonly ShowBreakthroughStatusUpdates: boolean;
    readonly ShowBreakthroughMetrics: boolean;
    readonly ShowBreakthroughActions: boolean;
    readonly ShowBreakthroughActionsComments: boolean;
    readonly Show2NdLevelBr: boolean;
    readonly Show2NdLevelBrStatusUpdates: boolean;
    readonly Show2NdLevelBrMetrics: boolean;
    readonly Show2NdLevelBrActions: boolean;
    readonly Show2NdLevelBrActionsComments: boolean;
    readonly PeriodTypeID: number | null;
    readonly EmbeddedMetrics: boolean;
    readonly ShowExcludedItems: boolean;
}

export interface StrategyBusinessReviewExportSettings extends Entity<'StrategyBusinessReviewExportSettings'> {
    readonly TextFilter: string[];
    readonly StrategyDescriptionIDs: number[];
    readonly ResponsibleUserIDs: number[];
    readonly ResponsibleCompanyFunctionIDs: number[];
    readonly ResponsibleVirtualTeamIDs: number[];
    readonly MilestoneCompanyFunctionIDs: number[];
    readonly MilestoneVirtualTeamIDs: number[];
    readonly ViewTypeID: enumerations.BusinessReviewViewType;
    readonly PeriodTypeID: number;
    readonly CurrentPeriodTitle: string;
    readonly CurrentPeriodStartDate: Date;
    readonly CurrentPeriodEndDate: Date;
    readonly PrevPeriodStartDate: Date;
    readonly PrevPeriodEndDate: Date;
    readonly PageOrientationID: enumerations.PageOrientation;
    readonly SvgImageDataUrls: string[];
}

export interface StrategyExecutionExportSettings extends Entity<'StrategyExecutionExportSettings'> {
    readonly ExportAll: boolean;
    readonly UseSimplifiedFormat: boolean;
    readonly StrategyDescriptionIDs: number[];
    readonly StrategicGoalIDs: number[];
    readonly StrategicGoalMilestoneIDs: number[];
    readonly StrategicProgramIDs: number[];
}

export interface TrendTopicComment extends Entity<'TrendTopicComment'> {
    readonly TopicID: number;
    readonly UserID: number;
    readonly DateAdded: Date;
    readonly Comment: string;
    readonly ReplyToCommentID: number | null;
}

export interface ContentWatchSettings extends Entity<'ContentWatchSettings'> { }

export interface TrendTopicType extends Entity<'TrendTopicType'> {
    readonly NameFI: string;
    readonly NameEN: string;
    readonly NameSE: string;
    readonly Name: string;
    readonly Shape: string;
    readonly ColorHex: string;
}

export interface TrendCategory extends Entity<'TrendCategory'> {
    readonly Identifier: string;
    readonly DescriptionFI: string;
    readonly DescriptionEN: string;
    readonly DescriptionSE: string;
    readonly Description: string;
    readonly Sort: number;
}

export interface TrendScope extends Entity<'TrendScope'> {
    readonly Identifier: string;
    readonly DescriptionFI: string;
    readonly DescriptionEN: string;
    readonly DescriptionSE: string;
    readonly Description: string;
    readonly Sort: number;
}

export interface TrendTopicReport extends Entity<'TrendTopicReport'> {
    readonly UserID: number;
    readonly ScopeID: number;
    readonly Name: string;
    readonly Description: string;
    readonly Probability: number;
    readonly CreatedDate: Date | null;
    readonly PESTELAreaID: number;
    readonly CoordinateWithinScopeScale: number | null;
    readonly CoordinateWithinPESTELScale: number | null;
    readonly ParentID: number | null;
    readonly TopicTypeID: number | null;
    readonly MarketID: number | null;
    readonly Comments: NetworkPaginationSlice<TrendTopicComment>;
    readonly MarketWatches: NetworkPaginationSlice<ContentWatchSettings>;
}

export interface Market extends Entity<'Market'> {
    readonly Name: string;
    readonly CreatorID: number;
    readonly Created: Date;
}

export interface MarketFreeNode extends Entity<'MarketFreeNode'> {
    readonly CreatorID: number;
    readonly Created: Date;
    readonly Name: string;
    readonly ParentNodeID: number | null;
    readonly Description: string;
}

export interface DashboardPageTemplate extends Entity<'DashboardPageTemplate'> {
    readonly Name: string;
    readonly Description: string;
    readonly ThumbnailURL: string;
    readonly ScreenshotURL: string;
    readonly TrackingLevelsRequired: number;
}

export interface DashboardPageReport extends NamedEntity<'DashboardPageReport'>, UserTrackingEntity<'DashboardPageReport'> {
    readonly GroupID: number | null;
    readonly CompanyFunctionID: number | null;
    readonly PageScreenshotImageURI: string;
    readonly RedirectUri: string;
    readonly IsPrivate: boolean;
    readonly IntegrationSourceID: number | null;
    readonly ProductFilteringEnabled: boolean;
    readonly GeoAreaFilteringEnabled: boolean;
    readonly CustomerFilteringEnabled: boolean;
    readonly TrackingLevel1FilteringEnabled: boolean;
    readonly TrackingLevel2FilteringEnabled: boolean;
    readonly TrackingLevel3FilteringEnabled: boolean;
    readonly TrackingLevel4FilteringEnabled: boolean;
    readonly CompanyLegalEntityFilteringEnabled: boolean;
    readonly CompanyFunctionFilteringEnabled: boolean;
    readonly PageScreenshotImageTaken: Date | null;

    readonly UserAccesses: DashboardPageUserAccess[];
    readonly Blocks: NetworkPaginationSlice<DashboardPageBlockReport>;

    readonly Custom: Entity<'DashboardPageReport'>['Custom'] & {
        isBusinessReviewPending?: boolean;
    };
}

export interface DashboardPageUserAccess extends Entity<'DashboardPageUserAccess'> {
    readonly DashboardPageID: number;
    readonly TargetUserID: number;
}

export interface DashboardPageBlockTemplate extends Entity<'DashboardPageBlockTemplate'> {
    readonly Name: string;
    readonly Description: string;
    readonly ThumbnailURL: string;
    readonly ScreenshotURL: string;
    readonly TrackingLevelsRequired: number;
}

export interface DashboardPageBlockIndicatorSerie<ET extends string = 'DashboardPageBlockTableSerieRowReport'> extends NamedEntity<ET> {
    readonly BlockID: number;
    readonly IndicatorID: number;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface PowerBIEmbedToken extends NetworkSynchronizable {
    readonly TokenID: string;
    readonly Token: string;
    readonly Expiration: Date;
}

export interface DashboardPageBlockPowerBIEmbed extends Entity<'DashboardPageBlockPowerBIEmbed'> {
    readonly BlockID: number;
    readonly WorkspaceIdentifier: string;
    readonly DashboardIdentifier: string;
    readonly EmbedURL: string;
    readonly ReportIdentifier: string;
    readonly Custom: Entity<'DashboardPageBlockPowerBIEmbed'>['Custom'] & {
        readonly embedToken: PowerBIEmbedToken;
    };
}

export interface DashboardPageBlockStrategy extends Entity<'DashboardPageBlockStrategy'> {
    readonly BlockID: number;
    readonly FilterJson: string;
    readonly NameVariantID: number | null;

    readonly Custom: Entity<'DashboardPageBlockStrategy'>['Custom'] & {
        readonly strategyContentSettings: any;
    };
}

export interface DashboardPageBlockCustomTableGraphSerieType extends SystemEnumerationEntity<'DashboardPageBlockCustomTableGraphSerieType', enumerations.DashboardPageBlockCustomTableGraphSerieType> {
    readonly Icon: string;
}

export interface DashboardPageBlockCustomTableQuery extends Entity<'DashboardPageBlockCustomTableQuery'> {
    readonly BlockID: number;
    readonly Query: string;
    readonly PivotByFirstColumn: boolean;
    readonly FilterByDateRange: boolean;
    readonly PieChartColumnID: number | null;
    readonly EditableByCustomers: boolean;
    readonly ShowGraphDefault: boolean;
    readonly GraphXAxisColumnID: number | null;
    readonly GraphYAxisColumnID: number | null;
    readonly DefaultStartDate: Date | null;
    readonly DefaultEndDate: Date | null;
    readonly GraphComparisonAxisColumnID: number | null;
    readonly GraphSerieTypeID: enumerations.DashboardPageBlockCustomTableGraphSerieType | null;
}

export interface DashboardPageBlockCustomTableColumnDefaultFilter extends SystemEnumerationEntity<'DashboardPageBlockCustomTableColumnDefaultFilter', enumerations.DashboardBlockCustomTableColumnDefaultFilter> {}

export interface DashboardPageBlockCustomTableColumn extends SortableEntity<'DashboardPageBlockCustomTableColumn'> {
    readonly BlockID: number;
    readonly SourceColumnName: string;
    readonly HeaderColumnName: string;
    readonly DecimalPlaces: number | null;
    readonly ValueSuffix: string;
    readonly AggregationFunction: string;
    readonly SortOrder: string;
    readonly UseHeatScale: boolean;
    readonly Filterable: boolean;
    readonly DefaultFilterByFirstValue: boolean | null;
    readonly TotalRowDividendColumnID: number | null;
    readonly TotalRowDivisorColumnID: number | null;
    readonly DefaultFilterID: enumerations.DashboardBlockCustomTableColumnDefaultFilter;
    readonly TextFilter: string;
}

export interface DashboardPageBlockMilestone extends Entity<'DashboardPageBlockMilestone'> {
    readonly BlockID: number;
    readonly MilestoneID: number | null;
    readonly ShowMetrics: boolean;
    readonly ShowActions: boolean;
}

export interface DashboardPageExportSettings extends Entity<'DashboardPageExportSettings'> {
    readonly DashboardPageID: number;
    readonly PageOrientationID: enumerations.PageOrientation;
    readonly InsightPeriodID: enumerations.InsightFilterPeriod;
    readonly InsightCustomPeriodStart: Date | null;
    readonly InsightCustomPeriodEnd: Date | null;
    readonly IsPrivate: boolean;
    readonly CoverLetter: string;
    readonly TaggedUserIDs: number[];
    readonly BlockImages: DashboardPageBlockImage[];
    readonly FileName: string;
}

export interface DashboardPageBlockImage extends Entity<'DashboardPageBlockImage'> {
    readonly BlockID: number;
    readonly ImageData: string;
    readonly Width: number;
    readonly Height: number;
}

export interface DashboardPageBlockReport extends NamedEntity<'DashboardPageBlockReport'> {
    readonly PageID: number | null;
    readonly AlertID: number | null;
    readonly TypeID: enumerations.DashboardBlockType;
    readonly Instruction: string;
    readonly GridXCellStart: number;
    readonly GridYCellStart: number;
    readonly WidthGridCells: number;
    readonly HeightGridCells: number;
    readonly PeriodTypeID: enumerations.PeriodType | null;
    readonly DefaultPeriodTypeID: enumerations.PeriodType;
    readonly UseLastFinancialPeriod: boolean;
    readonly RangeStartYearOffset: number | null;
    readonly RangeStartPeriodOffset: number | null;
    readonly Range: number | null;
    readonly CustomRangePastPeriods: number | null;
    readonly CustomRangeFuturePeriods: number | null;
    readonly SerieOrder: number | null;
    readonly NumberFormat: string;
    readonly IsWaterfall: boolean;
    readonly HasRollingTableColumns: boolean | null;
    readonly RollingTableColumnsPastDimensionID: enumerations.PerformanceIndicatorDimension | null;
    readonly RollingTableColumnsAlternativePastDimensionID: enumerations.PerformanceIndicatorDimension | null;
    readonly RollingTableColumnsFutureDimensionID: enumerations.PerformanceIndicatorDimension | null;
    readonly RollingTableColumnsAlternativeFutureDimensionID: enumerations.PerformanceIndicatorDimension | null;
    readonly RollingTableColumnsTotalEnabled: boolean | null;
    readonly HasTrackingLevelRows: boolean | null;
    readonly EmptyItemsHidden: boolean;
    readonly TrackingLevelRowsIndicatorID: number | null;
    readonly TrackingLevelRowsTrackingLevelID: enumerations.TrackingLevel | null;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly ProductFilteringEnabled: boolean;
    readonly GeoAreaFilteringEnabled: boolean;
    readonly CustomerFilteringEnabled: boolean;
    readonly TrackingLevel1FilteringEnabled: boolean;
    readonly TrackingLevel2FilteringEnabled: boolean;
    readonly TrackingLevel3FilteringEnabled: boolean;
    readonly TrackingLevel4FilteringEnabled: boolean;
    readonly CompanyLegalEntityFilteringEnabled: boolean;
    readonly CompanyFunctionFilteringEnabled: boolean;
    readonly UseLastSalesPeriod: boolean;
    readonly DefaultRollingTableColumnOffset: number;
    readonly CreatorID: number | null;
    readonly Created: Date;
    readonly ForesightScenarioID: number | null;
    readonly Comments: NetworkPaginationSlice<DashboardPageBlockComment>;

    // TODO fill proper types
    readonly CartesianSeries: DashboardPageBlockIndicatorSerie[];
    readonly ComparisonColumnSeries: DashboardPageBlockIndicatorSerie[];
    readonly ComparisonBarSeries: DashboardPageBlockIndicatorSerie[];
    readonly PieSeries: DashboardPageBlockIndicatorSerie[];
    readonly NumberSeries: DashboardPageBlockIndicatorSerie[];
    readonly SolidGaugeSeries: DashboardPageBlockIndicatorSerie[];
    readonly TableRollingComparisons: DashboardPageBlockTableRollingComparison[];
    readonly TableDataColumns: DashboardPageBlockTableDataColumn[];
    readonly TableComparisonColumns: DashboardPageBlockTableComparisonColumn[];
    readonly TableSumColumns: DashboardPageBlockTableSumColumn[];
    readonly TableSerieRows: (DashboardPageBlockTableSerieRowReport | HackedDashboardPageBlockTableSerieRowReport)[];
    readonly TableComparisonRows: DashboardPageBlockTableComparisonRow[];
    readonly TableEmptyRows: DashboardPageBlockTableEmptyRow[];
    readonly PowerBIEmbed: DashboardPageBlockPowerBIEmbed;
    readonly CustomTableQuery: DashboardPageBlockCustomTableQuery;
    readonly CustomTableColumns: DashboardPageBlockCustomTableColumn[];
    readonly StrategySettings: DashboardPageBlockStrategy;
    readonly MilestoneSettings: DashboardPageBlockMilestone;
    readonly CustomTableData: NetworkObject<string>;

    readonly Custom: Entity<'DashboardPageBlockReport'>['Custom'] & {
        subtypeID: number | null;
        currentDate: Date;
        blockImage?: DashboardPageBlockImage | null;
    };
}

export interface DashboardPageBlockComment extends Entity<'DashboardPageBlockComment'> {
    BlockID: number;
    UserID: number;
    DateAdded: Date;
    Comment: string;
    ReplyToCommentID: number | null;
}

export interface DashboardPageBlockTableRollingComparison extends Entity<'DashboardPageBlockTableRollingComparison'> {
    readonly BlockID: number;
    readonly Name: string;
    readonly ComparisonTargetID: enumerations.PerformanceIndicatorComparisonTarget;
    readonly ComparisonCalculationID: enumerations.PerformanceIndicatorComparisonCalculation;
    readonly Custom: Entity<'DashboardPageBlockTableRollingComparison'>['Custom'] & {
        readonly customNameEnabled: boolean;
    };
}

export interface DashboardPageBlockTableDataColumn extends SortableEntity<'DashboardPageBlockTableDataColumn'> {
    readonly BlockID: number;
    readonly Name: string;
    readonly PastDimensionID: enumerations.PerformanceIndicatorDimension;
    readonly AlternativePastDimensionID: enumerations.PerformanceIndicatorDimension | null;
    readonly FutureDimensionID: enumerations.PerformanceIndicatorDimension;
    readonly AlternativeFutureDimensionID: enumerations.PerformanceIndicatorDimension | null;
    readonly PointCalculationID: enumerations.PerformanceIndicatorPointCalculation;
    readonly YearOffset: number;
    readonly PeriodOffset: number;
    readonly TrackingTargetSystemRegisterID: enumerations.TrackingLevel | null;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface DashboardPageBlockTableComparisonColumn extends SortableEntity<'DashboardPageBlockTableComparisonColumn'> {
    readonly BlockID: number;
    readonly Name: string;
    readonly FromColumnID: number;
    readonly FromIndicatorID: number | null;
    readonly ToColumnID: number;
    readonly ToIndicatorID: number | null;
    readonly ComparisonCalculationID: enumerations.PerformanceIndicatorComparisonCalculation;
    readonly OveruseWarningsEnabled: boolean;
}

export interface DashboardPageBlockTableSumColumn extends SortableEntity<'DashboardPageBlockTableSortColumn'> {
    readonly BlockID: number;
    readonly Name: string;
    readonly FromColumnID: number;
    readonly ToColumnID: number;
}

export interface DashboardPageBlockTableSerieRowExpandedIndicator extends Entity<'DashboardPageBlockTableSerieRowExpandedIndicator'> {
    readonly RowID: number;
    readonly IndicatorID: number;
}

export interface DashboardPageBlockTableSerieRowReport extends DashboardPageBlockIndicatorSerie<'DashboardPageBlockTableSerieRowReport'>, SortableEntity<'DashboardPageBlockTableSerieRowReport'> {
    readonly BackgroundColor: string;
    readonly BordersVisible: boolean;
    readonly IsBold: boolean;
    readonly HighlightNegative: boolean;
    readonly TrackingTargetSystemRegisterID: enumerations.TrackingLevel | null;
    readonly ExpandedIndicators: DashboardPageBlockTableSerieRowExpandedIndicator[];
}

export interface HackedDashboardPageBlockTableSerieRowReport extends DashboardPageBlockTableSerieRowReport {
    // following fields don't exist in back end nor do they really work, a dirty-ish hack for foresight scenarios
    readonly PastIndicatorID: number;
    readonly FutureIndicatorID: number;
    readonly ComparisonTargetID: enumerations.PerformanceIndicatorComparisonTarget.INDICATOR;
    readonly ComparisonCalculationID: number | null;
    readonly PastComparisonIndicatorID: number | null;
    readonly FutureComparisonIndicatorID: number | null;
}

export interface DashboardPageBlockTableComparisonRow extends SortableEntity<'DashboardPageBlockTableComparisonRow'> {
    readonly BlockID: number;
    readonly Name: string;
    readonly FromRowID: number;
    readonly ToRowID: number;
    readonly ComparisonCalculationID: enumerations.PerformanceIndicatorComparisonCalculation | null;
    readonly BackgroundColor: string;
    readonly BordersVisible: boolean;
    readonly IsBold: boolean;
    readonly HighlightNegative: boolean;
    readonly IsSummedRow: boolean;
    readonly FromSumRowID: number | null;
    readonly ToSumRowID: number | null;
    readonly Hidden: boolean;
}

export interface DashboardPageBlockTableEmptyRow extends SortableEntity<'DashboardPageBlockTableEmptyRow'> {
    readonly BlockID: number;
    readonly BackgroundColor: string;
    readonly BordersVisible: boolean;
}

export interface PerformancePerspectiveGroup extends SystemEnumerationEntity<'PerformancePerspectiveGroup', enumerations.PerformancePerspectiveGroup> {
    readonly PerspectiveID: enumerations.PerformancePerspective;
    readonly ConnectedToGroupID: number | null;
}

export interface PerformancePerspective extends SystemEnumerationEntity<'PerformancePerspective', enumerations.PerformancePerspective> { }

export interface StrategyDescriptionReport extends Entity<'StrategyDescriptionReport'> {
    readonly Custom: Entity<'StrategyDescriptionReport'>['Custom'] & {
        createdInRoadmap?: boolean;
    };

    readonly FunctionID: number | null;
    readonly CreatorID: number;
    readonly DateAdded: Date;
    readonly Name: string;
    readonly VisibleToAll: boolean;
    readonly FatherID: number | null;
    readonly StatusID: enumerations.StrategyDescriptionStatus;
    readonly StrategyNamingTypeID: enumerations.StrategyNamingType;
    readonly CustomLabel: string;
    readonly StrategyDefaultCustomLabel: string;
    readonly GoalDefaultCustomLabel: string;
    readonly MilestoneDefaultCustomLabel: string;
    readonly ProgramDefaultCustomLabel: string;
    readonly SubProgramDefaultCustomLabel: string;
    readonly ImplementedGoalID: number | null;
    readonly ImplementedCriticalSuccessFactorID: number | null;
    readonly ImplementedMilestoneID: number | null;
    readonly PublishingTypeID: enumerations.StrategyPublishingType;
    readonly ProgressUpdatesAvailable: boolean;
    readonly EditableByAll: boolean;
    readonly ExcludedFromBusinessReview: boolean;
    readonly BigPictureGoalPlotBands: boolean;
    readonly BigPictureCustomFieldID: number | null;

    readonly Participators: StrategyDescriptionParticipator[];
    readonly Admins: StrategyDescriptionAdministrator[];
    readonly Metrics: GoalMetricReport[];

    readonly CanUserEditStrategy: boolean;
    readonly CanUserEditCreator: boolean;
    readonly CanUserPublishStrategy: boolean;
    readonly CanUserEditSelections: boolean;
    readonly CanUserEditDescriptions: boolean;
    readonly CanUserEditDashboardLinks: boolean;
    readonly CanUserAddSubStrategy: boolean;
    readonly CanUserAddFreeNode: boolean;
    readonly CanUserAddGoal: boolean;
    readonly CanUserEditMetrics: boolean;
    readonly CanUserExcludeStrategyFromBusinessReview: boolean;

    readonly IsArtificial: boolean;

    [index: string]: any;
}

export interface StrategyNetworkArray extends NetworkArray<StrategyDescriptionReport> {
    readonly Custom: NetworkArray<StrategyDescriptionReport>['Custom'] & {
        copy?: boolean;
    };
}

export interface StrategyDescriptionParticipator extends Entity<'StrategyDescriptionParticipator'> {
    readonly StrategyDescriptionID: number;
    readonly UserID: number;
    readonly InviteSent: Date | null;
}

export interface StrategyDescriptionAdministrator extends Entity<'StrategyDescriptionAdministrator'> {
    readonly StrategyDescriptionID: number;
    readonly UserID: number;
}

export interface StrategyDescriptionPerformanceIndicator extends Entity<'StrategyDescriptionPerformanceIndicator'> {
    readonly StrategyDescriptionID: number;
    readonly IndicatorID: number;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface StrategyPageBlock extends Entity<'StrategyPageBlock'> {
    readonly StrategyDescriptionID: number;
    readonly GridXCellStart: number;
    readonly GridYCellStart: number;
    readonly WidthGridCells: number;
    readonly HeightGridCells: number;
    readonly TypeID: number;
    readonly DashboardBlockID: number | null;
}
export interface StrategyPageMemoBlock extends Entity<'StrategyPageMemoBlock'> {
    readonly StrategyPageBlockID: number;
    readonly Title: string;
    readonly ContentText: string;
}

export interface DashboardPageBlockType extends SystemEnumerationEntity<'DashboardPageBlockType', enumerations.DashboardBlockType> {
    readonly IconIdentifier: string;
    readonly Expandable: boolean;
    readonly Dateable: boolean;
    readonly Rangeable: boolean;
    readonly Orderable: boolean;
    readonly Sort: number;
}

export interface PerformanceIndicatorDimension extends SystemEnumerationEntity<'PerformanceIndicatorDimension', enumerations.PerformanceIndicatorDimension> { }

export interface PerformanceIndicatorComparisonTarget extends SystemEnumerationEntity<'PerformanceIndicatorComparisonTarget', enumerations.PerformanceIndicatorComparisonTarget> {
    readonly DimensionID: enumerations.PerformanceIndicatorDimension | null;
}

export interface PerformanceIndicatorComparisonCalculation extends SystemEnumerationEntity<'PerformanceIndicatorComparisonCalculation', enumerations.PerformanceIndicatorComparisonCalculation> { }

export interface PerformanceIndicatorConversation extends Entity<'PerformanceIndicatorConversation'> {
    readonly IndicatorID: number;
    readonly UserID: number;
    readonly DateAdded: Date;
    readonly Comment: string;
    readonly ReplyToCommentID: number | null;
}

export interface PerformanceIndicatorConversationReport extends Entity<'PerformanceIndicatorConversationReport'> {
    readonly Comments: PerformanceIndicatorConversation[];
}

export interface StrategicProgramConnection extends Entity<'StrategicProgramConnection'> {
    readonly SourceProgramID: number;
    readonly StrategyDescriptionID: number | null;
    readonly StrategicGoalID: number | null;
    readonly StrategicGoalMilestoneID: number | null;
    readonly StrategicSelectionID: number | null;
    readonly TargetProgramID: number | null;
}

export interface StrategyFreeNodeReport extends Entity<'StrategyFreeNodeReport'> {
    readonly AddedBy: number;
    readonly Added: Date;
    readonly Name: string;
    readonly ParentDescriptionID: number | null;
    readonly ParentGoalID: number | null;
    readonly ParentNodeID: number | null;
    readonly DashboardPageID: number | null;
    readonly IsSelection: boolean;
    readonly Description: string;
    readonly ParentMilestoneID: number | null;
    readonly ParentProgramID: number | null;
    readonly IsMemo: boolean;
    readonly IsAttachment: boolean;
    readonly TargetURL: string;

    readonly CanUserEditFreeNode: boolean;
    readonly CanUserAddFreeNode: boolean;
    readonly CanUserEditDescriptions: boolean;
    readonly CanUserEditSelections: boolean;
}

export interface StrategyPageBlockType extends SystemEnumerationEntity<'StrategyPageBlockType', enumerations.StrategyPageBlockType> {
    readonly IconIdentifier: string;
}

export interface IntegrationSource extends SystemEnumerationEntity<'IntegrationSource', enumerations.IntegrationSource> {
    readonly WebURL: string;
    readonly FlavorText: string;
}

export interface IntegrationSourceStatus extends SystemEnumerationEntity<'IntegrationSourceStatus', enumerations.IntegrationSourceStatus> { }

export interface IntegrationSourceCompanyStatus extends Entity<'IntegrationSourceCompanyStatus'> {
    readonly SourceID: enumerations.IntegrationSource;
    readonly SourceStatusID: enumerations.IntegrationSourceStatus;
    readonly IsActive: boolean;
}

export interface SheetGenerationSettings {
    readonly DimensionID: number | null;
    readonly HistoricValuesSourceDimensionID: number | null;
    readonly HistoricValuesSourceYear: number | null;
    readonly IncludeHistoricValues: boolean;
    readonly Indicators: SheetGenerationSettingsIndicator[];
    readonly IsRolling: boolean;
    readonly IsTemplate: boolean;
    readonly Name: string;
    readonly PeriodID: number;
    readonly RemindedUserID: number | null;
    readonly ReminderPeriodTypeID: number | null;
    readonly ResponsibleUserID: number;
    readonly SendReminder: boolean;
    readonly StartDate: Date | null;
}

export interface SheetGenerationSettingsIndicator extends Entity<'SheetGenerationSettingsIndicator'> {
    readonly ActualWriteRuleID: number;
    readonly BudgetImportRuleID: number;
    readonly SourceID: number | null;
}

export interface UserLocal extends Entity<'UserLocal'> {
    readonly FirstName: string;
    readonly LastName: string;
    readonly Email: string;
    readonly MobilePhoneNumber: string;
    readonly SystemLanguageID: number;
    readonly TimeZoneIdentifier: string;
    readonly DirectManagerID: number;
    readonly ProfileImageURI: string;
    readonly HasAccessTo: number;
    readonly AdministrationUserTypeID: number;
    readonly CompanyFunctionID: number;
    readonly Title: string;
    readonly SystemUserTypeID: number;
}

export interface UserListDataReport extends Entity<'UserListDataReport'> {
    readonly FirstName: string;
    readonly LastName: string;
    readonly SystemUserTypeName: string;
    readonly Email: string;
    readonly Title: string;
    readonly MobilePhoneNumber: string;
    readonly ImmediateSuperior: string;
    readonly LastLoginDate: Date | null;
}

export interface UserActivityItem extends Entity<'UserActivityItem'> {
    readonly FirstName: string;
    readonly LastName: string;
    readonly UserEmail: string;
    readonly TeamName: string;
    readonly RoleIdentifier: string;
    readonly CreatedDate: Date;
    readonly LastLoginDate: Date | null;
    readonly LoginCount: number;
    readonly CreatedActivityCount: number;
    readonly UpdatedActivityCount: number;
    readonly UserActivityStatus: number;
}

export interface UserActivityReport extends Entity<'UserActivityReport'> {
    readonly UserActivityItems: UserActivityItem[];
    readonly AvgUserActivityStatus: string;
    readonly PeriodStartDate: Date;
    readonly PeriodEndDate: Date;
}

export interface DecisionPollReport extends Entity<'DecisionPollReport'> {
    readonly CreatedBy: number;
    readonly Created: Date;
    readonly Name: string;
    readonly Introduction: string;
    readonly TypeID: enumerations.DecisionPollType;
    readonly ReplyOptionTypeID: number;
    readonly DeadLineTimeStamp: Date;
    readonly InitiativeID: number;
    readonly ActionID: number | null;
    readonly MilestoneID: number | null;
    readonly SWOTInboxID: number | null;
    readonly MeetingID: number | null;
    readonly InsightID: number | null;

    readonly Respondents: DecisionPollRespondent[];

    readonly Custom: Entity<'DecisionPollReport'>['Custom'] & {
        readonly targetObjectName?: string;
        readonly answerInDaysCount?: number;
    };
}

export interface DecisionPollRespondent extends Entity<'DecisionPollRespondent'> {
    readonly DecisionPollID: number;
    readonly UserID: number;
    readonly ReplyOptionTypeOptionID: number | null;
    readonly ReplyDate: Date | null;
    readonly ReplyComment: string;
}

export interface DecisionPollReplyOptionTypeReport extends Entity<'DecisionPollReplyOptionTypeReport'> {
    readonly Name: string;
    readonly ResultsBooleanDisplay: boolean;

    readonly Options: DecisionPollReplyOptionTypeOption[];
}

export interface DecisionPollReplyOptionTypeOption extends Entity<'DecisionPollReplyOptionTypeOption'> {
    readonly ReplyOptionTypeID: number;
    readonly Value: number;
    readonly Name: string;
}

export interface DecisionTopicReport extends Entity<'DecisionTopicReport'> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly TypeID: number;
    readonly PhaseID: number | null;
    readonly StatusID: enumerations.DecisionTopicStatus;
    readonly Sort: number;
    readonly RequireDecision: boolean;
    readonly Name: string;
    readonly Description: string;
    readonly FunctionID: number;
    readonly AttachmentFileIdentifier: string;
    readonly AttachmentFileName: string;
    readonly ExternalSourceLink: string;
    readonly MilestoneID: number | null;
    readonly StrategicGoalID: number | null;
    readonly StrategyDescriptionID: number | null;
    readonly AttachmentFileData: string;
    readonly Comments: DecisionTopicComment[];
}

export interface DecisionTopicComment extends Entity<'DecisionTopicComment'> {
    readonly TopicID: number;
    readonly UserID: number;
    readonly DateAdded: Date;
    readonly Comment: string;
    readonly ReplyToCommentID: number | null;
}

export interface SWOTInbox extends Entity<'SWOTInbox'> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly SWOTAreaID: number;
    readonly Comment: string;
    readonly IsClosed: boolean;
    readonly CompanyFunctionID: number | null;
    readonly Title: string;
    readonly BlobStorageURI: string;
    readonly BreakthroughID: number;
}

export interface DashboardGroup extends NamedEntity<'DashboardGroup'> {
    readonly Identifier: string;
    readonly IsSystem: boolean;
    readonly AddedByUserID: number;
}

export interface DashboardPageHomeSettings extends Entity<'DashboardPageHomeSettings'> {
    readonly PageID: number;
    readonly FunctionID: number | null;
    readonly UserID: number | null;
}

export interface IntegrationFinancialUploadBatchFileReport extends Entity<'IntegrationFinancialUploadBatchFileReport'> {
    readonly BatchID: number;
    readonly FileBlobURI: string;
    readonly FileName: string;
    readonly MimeType: string;
    readonly ExcelDetails: IntegrationFinancialUploadBatchExcelFile;
}

export interface IntegrationFinancialUploadBatchExcelFile extends Entity<'IntegrationFinancialUploadBatchExcelFile'> {
    readonly BatchFileID: number;
    readonly SheetName: string;
    readonly DateRow: number;
    readonly DataStartCell: string;
    readonly DimensionID: enumerations.PerformanceIndicatorDimension;
    readonly PeriodTypeID: enumerations.PeriodType;
    readonly NegativeExpenses: boolean;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly DynamicTrackingLevelID: number | null;
    readonly DynamicTrackingLevelColumn: string;
    readonly FirstColumnAccountNumbers: boolean;
    readonly CurrencyRateConversion: number | null;
    readonly YearToDateValues: boolean;
    readonly BalanceSheetConversionRate: number | null;
    readonly ImportAccountingDataEntries: boolean;
}

export interface DecisionTopicType extends Entity<'DecisionTopicType'> {
    readonly Name: string;
    readonly NameEN: string;
    readonly NameFI: string;
    readonly NameSE: string;
}

export interface DecisionTopicPhase extends SortableEntity<'DecisionTopicPhase'> {
    readonly IsStartPhase: boolean;
    readonly Name: string;
    readonly NameEN: string;
    readonly NameFI: string;
    readonly NameSE: string;
}

export interface SheetExcelGenerationIndicatorDepthRule extends SystemEnumerationEntity<'SheetExcelGenerationIndicatorDepthRule', enumerations.ExcelGenerationIndicatorDepthRule> { }
export interface SheetExcelGenerationValueWriteRule extends SystemEnumerationEntity<'SheetExcelGenerationValueWriteRule', enumerations.ExcelGenerationValueWriteRule> { }

export interface SheetExcelGenerationSettings {
    readonly IndicatorGroupID: enumerations.PerformanceIndicatorGroup;
    readonly CustomIndicatorIDs: number[];
    readonly IndicatorDepthRuleID: enumerations.ExcelGenerationIndicatorDepthRule;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly StartDate: Date;
    readonly EndDate: Date;
    readonly ValueWriteRuleID: enumerations.ExcelGenerationValueWriteRule;
}

export interface ERPDimensionMapping extends Entity<'ERPDimensionMapping'> {
    readonly AccountingDimension1: string;
    readonly AccountingDimension2: string;
    readonly AccountingDimension3: string;
    readonly AccountingDimension4: string;
    readonly AccountingDimension5: string;
    readonly AccountingDimension6: string;
    readonly Dimension1SyncHandleTypeID: number | null;
    readonly Dimension2SyncHandleTypeID: number | null;
    readonly Dimension3SyncHandleTypeID: number | null;
    readonly Dimension4SyncHandleTypeID: number | null;
    readonly Dimension5SyncHandleTypeID: number | null;
    readonly Dimension6SyncHandleTypeID: number | null;
    readonly Dimension1SyncTargetRegisterID: number | null;
    readonly Dimension2SyncTargetRegisterID: number | null;
    readonly Dimension3SyncTargetRegisterID: number | null;
    readonly Dimension4SyncTargetRegisterID: number | null;
    readonly Dimension5SyncTargetRegisterID: number | null;
    readonly Dimension6SyncTargetRegisterID: number | null;
    [index: string]: any;
}

export interface IntegrationDataMappingRule extends Entity<'IntegrationDataMappingRule'> {
    readonly DataMappingDimensionID: enumerations.IntegrationDataMappingDimension;
    readonly SourceID: enumerations.IntegrationDataMappingSource;
    readonly MatchRuleID: enumerations.IntegrationDataMappingMatchRule;
    readonly MatchRuleData: string;
    readonly TargetRuleData: string;
    readonly Sort: number;
    readonly DataMappingTypeID: enumerations.IntegrationDataMappingType;
    readonly SourceLegalEntityID: number | null;
}

export interface IntegrationDataMappingMatchRule extends SystemEnumerationEntity<'IntegrationDataMappingMatchRule', enumerations.IntegrationDataMappingMatchRule> { }

export interface IntegrationDataMappingSource extends SystemEnumerationEntity<'IntegrationDataMappingSource', enumerations.IntegrationDataMappingSource> {
    readonly DataMappingTypeID: enumerations.IntegrationDataMappingType;
}

export interface IntegrationFinancialUploadBatch extends Entity<'IntegrationFinancialUploadBatch'> {
    readonly IntegrationSourceID: enumerations.IntegrationSource;
    readonly Added: Date;
    readonly AddedBy: number;
    readonly PeriodTimeStart: Date;
    readonly PeriodTimeEnd: Date;
    readonly StatusID: enumerations.IntegrationFinancialUploadBatchStatus;
    readonly CompanyLegalEntityID: number | null;
}

export interface ERPFivaldiIntegrationSettings extends Entity<'ERPFivaldiIntegrationSettings'> {
    readonly Created: Date;
    readonly CreatedBy: number | null;
    readonly FivaldiUserName: string;
    readonly FivaldiPassword: string;
    readonly FivaldiSchemaNumber: number | null;
    readonly FivaldiOrganizationIdentifier: string;
    readonly CredentialsPrivate: boolean | null;
    readonly SyncActive: boolean | null;
    readonly SyncProducts: boolean | null;
    readonly SyncProductsLastDate: Date | null;
    readonly SyncCustomers: boolean;
    readonly SyncCustomersLastDate: Date | null;
    readonly SyncOrders: boolean;
    readonly SyncOrdersLastDate: Date | null;
    readonly SyncSales: boolean | null;
    readonly SyncSalesLastDate: Date | null;
    readonly SyncAccounting: boolean;
    readonly SyncAccountingLastDate: Date | null;
    readonly CompanyLegalEntityID: number;
    readonly IsInitialRunDone: boolean;
}

export interface ERPNetvisorIntegrationSettings extends Entity<'ERPNetvisorIntegrationSettings'> {
    readonly Name: string;
    readonly OrganisationIdentifier: string;
    readonly CreatedByUserID: number;
    readonly CompanyLegalEntityID: number;
    readonly IntegrationKey: string;
    readonly IntegrationHash: string;
    readonly IsTransferActive: boolean;
    readonly LastAccountingDataFetchTimestamp: Date;
    readonly LastInvoicingDataFetchTimestamp: Date;
    readonly LastCustomerDataFetchTimestamp: Date;
    readonly LastProductDataFetchTimestamp: Date;
    readonly LastOrderDataFetchTimestamp: Date | null;
    readonly LockPeriodID: enumerations.ERPNetvisorIntegrationLockPeriod;
    readonly AutomaticUpdate: boolean;
}

export interface ERPNetvisorIntegrationLockPeriod extends SystemEnumerationEntity<'ERPNetvisorIntegrationLockPeriod', enumerations.ERPNetvisorIntegrationLockPeriod> {}

export interface ERPProcountorIntegrationSettings extends Entity<'ERPProcountorIntegrationSettings'> {
    readonly AuthorizedToUserID: number;
    readonly Crated: Date;
    readonly AccessToken: string;
    readonly AccessTokenExpirationDate: Date | null;
    readonly RefreshToken: string;
    readonly LastUpdated: Date | null;
    readonly SyncActive: boolean;
    readonly SyncProductsLastDate: Date | null;
    readonly SyncCustomersLastDate: Date | null;
    readonly SyncInvoicesLastDate: Date | null;
    readonly SyncAccountingLastDate: Date | null;
    readonly SyncProducts: boolean;
    readonly SyncCustomers: boolean;
    readonly IsInitialRunDone: boolean;
    readonly CompanyLegalEntityID: number;
    readonly LastReceiptIdAfterError: number | null;
    readonly LastInvoiceIdAfterError: number | null;
}

export interface InvoicingDataReceivableReport extends Entity<'InvoicingDataReceivableReport'> {
    readonly InvoiceNumber: string;
    readonly InvoicingCustomerName: string;
    readonly DueDate: Date;
    readonly Amount: number;
}

export interface InvoicingDataReceivableReportPaginationSlice extends NetworkPaginationSlice<InvoicingDataReceivableReport> {
    TotalAmount: number;
}

export interface VATPeriodType extends SystemEnumerationEntity<'VATPeriodType', enumerations.VATPeriodType> { }

export interface CashflowForecastSettings<T extends string = 'CashflowForecastSettings' | 'CashflowForecastSettingsReport' | 'CashflowForecastDataReport'> extends Entity<T> {
    readonly AccountBalance: number;
    readonly AccountBalanceLimit: number;
    readonly EstimatedSalesReach: number;
    readonly EstimatedCostsReach: number;
    readonly ForecastEnabled: boolean;
    readonly UseForecastModel: boolean;
    readonly VATSimulationPeriodTypeID: enumerations.VATPeriodType | null;
    readonly CashIndicatorID: number;
    readonly PeriodProfitIndicatorID: number;
    readonly EquityIndicatorID: number;
    readonly VATIndicatorID: number;
    readonly EquityVisible: boolean;
}

export interface CashflowForecastIndicatorDistributionEntry extends Entity<'CashflowForecastIndicatorDistributionEntry'> {
    readonly ConnectionID: number;
    readonly MonthOffset: number;
    readonly Amount: number;
}

export interface CashflowForecastIndicatorConnectionReport extends Entity<'CashflowForecastIndicatorConnectionReport'> {
    readonly ProfitSourceIndicatorID: number;
    readonly BalanceTargetIndicatorID: number;
    readonly VAT: number;
    readonly Distribution: CashflowForecastIndicatorDistributionEntry[];
}

export interface CashflowForecastSettingsReport extends CashflowForecastSettings<'CashflowForecastSettingsReport'> {
    readonly IndicatorConnections: CashflowForecastIndicatorConnectionReport[];
}


export interface CashflowForecastDataReport extends CashflowForecastSettings<'CashflowForecastDataReport'> {
    readonly ReceivablesTurnoverIndicatorID: number | null;
    readonly PayablesTurnoverIndicatorID: number | null;
    readonly Receivables: InvoicingDataReceivableReportPaginationSlice;
}

export interface CategoryReport extends SystemEnumerationEntity<'CategoryReport', enumerations.Category> {
    readonly IsActive: boolean;
}

export interface InsightImage extends Entity<'InsightImage'> {
    readonly InsightID: number;
    readonly ImageURI: string;
    readonly MimeType: string;
    readonly FileName: string;
    readonly IsImage: boolean;
    readonly IsPdf: boolean;

    readonly Custom: Entity<'InsightImage'>['Custom'] & {
        readonly pending?: boolean;
        readonly isCompanyImage?: boolean;
        readonly imageDataPending?: boolean;
        readonly imageData?: string;
    };
}

export interface InsightReactionLink extends Entity<'InsightReactionLink'> {
    readonly InsightID: number;
    readonly InsightReactionTypeID: number;
    readonly Created: Date;
    readonly CreatorID: number;
}

export interface InsightTaggedUser extends Entity<'InsightTaggedUser'> {
    readonly InsightID: number;
    readonly UserID: number;
}

export interface InsightLinkReport extends Entity<'InsightLinkReport'> {
    readonly InsightID: number;
    readonly CategoryID: number;
    readonly BusinessModelCanvasItemID: number | null;
    readonly DashboardPageID: number | null;
    readonly DashboardPageBlockID: number | null;
    readonly NewsEntryID: number | null;
    readonly StrategyDescriptionID: number | null;
    readonly StrategicGoalMilestoneID: number | null;
    readonly StrategicGoalID: number | null;
    readonly StrategicGoalMilestoneStatusUpdateID: number | null;
    readonly StrategicProgramID: number | null;
    readonly StrategicProgramStatusUpdateID: number | null;
    readonly WorkshopWidgetID: number | null;
    readonly WorkspaceMindmapID: number | null;
    readonly DrillTitle: string;
    readonly DrillUri: string;
    readonly DrillUriIconIdentifier: string;
}

export interface InsightReactionType extends SystemEnumerationEntity<'InsightReactionType', enumerations.InsightReactionType> { }
export interface InsightType extends SystemEnumerationEntity<'InsightType', enumerations.InsightType> { }

export interface InsightReport extends Entity<'InsightReport'> {
    readonly Created: Date;
    readonly CreatorID: number;
    readonly TypeID: enumerations.InsightType;
    readonly LastUpdate: Date;
    readonly ReplyToInsightID: number | null;
    readonly MainParentInsightID: number | null;
    readonly RecentReplyInsightID: number | null;
    readonly Description: string;
    readonly ExternalLinkURI: string;
    readonly BusinessEmoji: string;
    readonly Sort: number | null;
    readonly IsPrivate: boolean;
    readonly TargetPeriodDate: Date | null;
    readonly CustomUserName: string;

    readonly Images: InsightImage[];
    readonly Reactions: InsightReactionLink[];
    readonly TaggedUsers: InsightTaggedUser[];
    readonly Links: InsightLinkReport[];
    readonly Poll: DecisionPollReport | null;

    readonly Custom: Entity<'InsightReport'>['Custom'] & {
        readonly domElementForDefaultScreenshot?: Element;
    };
}

export interface InsightFilter extends Entity<'InsightFilter'> {
    readonly CategoryIDs: number[];
    readonly BusinessModelCanvasItemIDs: number[];
    readonly DashboardPageIDs: number[];
    readonly DashboardPageBlockIDs: number[];
    readonly MarketIDs: number[];
    readonly StrategyDescriptionIDs: number[];
    readonly StrategicGoalIDs: number[];
    readonly StrategicGoalMilestoneIDs: number[];
    readonly StrategicProgramIDs: number[];
    readonly ResponsibleUserIDs: number[];
    readonly WorkshopWidgetIDs: number[];
    readonly WorkspaceMindmapIDs: number[];
    readonly TaggedUserIDs: number[];
    readonly InsightTypeIDs: number[];
}

export interface InsightNetworkArray extends NetworkArray<InsightReport> {
    readonly Custom: NetworkArray<InsightReport>['Custom'] & {
        readonly feedKey?: enumerations.InsightFeedKey;
        readonly feedInitialized?: boolean;
        readonly currentUserID?: number; // userd for new insight broadcasting
        readonly initializedFilters?: InsightFilter;
        readonly selectedFilter?: InsightFilter;
    };
}

export interface InsightCompanyData extends Entity<'InsightCompanyData'> {
    readonly Domain: string;
    readonly Name: string;
    readonly LegalName: string;
    readonly Description: string;
    readonly CountryCode: string;
    readonly FoundedYear: number | null;
    readonly Employees: number | null;
    readonly Raised: number | null;
    readonly AnnualRevenue: number | null;
    readonly URI: string;
    readonly ImageURI: string;
}

export interface InsightDefaultExcludedDomain extends Entity<'InsightDefaultExcludedDomain'> {
    readonly Created: Date;
    readonly CreatorID: number;
    readonly Domain: string;
}

export interface DecisionReport extends Entity<'DecisionReport'> {
    readonly DecisionTopicID: number | null;
    readonly Description: string;
    readonly Reason: string;
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly FollowUpByUserID: number | null;
    readonly StatusID: enumerations.DecisionStatus;
    readonly ClosedDate: Date | null;
    readonly FollowUpMessage: string;
    readonly MeetingSectionTopicID: number | null;
    readonly TrackingTypeID: enumerations.DecisionTrackingType;
    readonly StrategicGoalMilestoneID: number | null;
    readonly StrategicProgramID: number | null;
    readonly ClosedBy: number | null;
    readonly ClosedComment: string;
    readonly ClosedAtMeetingID: number | null;
    readonly DueDate: Date | null;
    readonly StrategicGoalID: number | null;

    readonly MeetingID: number | null;
    readonly MeetingSectionID: number | null;
    readonly CompanyFunctionID: number | null;
}

export interface DecisionTrackingType extends SystemEnumerationEntity<'DecisionTrackingType', enumerations.DecisionTrackingType> { }


export interface SheetForecastFileStatus extends SystemEnumerationEntity<'SheetForecastFileStatus', enumerations.SheetForecastFileStatus> { }

export interface SheetForecastFileUserAccess extends Entity<'SheetForecastFileUserAccess'> {
    readonly FileID: number;
    readonly UserID: number;
}

export interface SheetForecastFileReport extends Entity<'SheetForecastFileReport'> {
    readonly ForecastID: number;
    readonly Name: string;
    readonly Note: string;
    readonly FatherID: number | null;
    readonly InputMethodID: number | null;
    readonly ExcelConfigurationID: number | null;
    readonly GoogleConfigurationID: number | null;
    readonly IsRolling: boolean;
    readonly RollingNotificationsPeriodTypeID: number | null;
    readonly RollingNotificationsDashboardPageID: number | null;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly ResponsibleUserID: number | null;
    readonly IsPrivate: boolean;
    readonly StatusID: enumerations.SheetForecastFileStatus;
    readonly ManagerNote: string;
    readonly ResponsibleUserNote: string;
    readonly LastUserUpdate: Date | null;
    readonly LastReminderSentDate: Date | null;
    readonly TypeID: number;
    readonly ErrorMessage: string;
    readonly LastErrorDate: Date | null;
    readonly SheetMessageColumnFromBudgetManager: string;
    readonly SheetMessageStatusColumnToBudgetManager: string;
    readonly SheetMessageColumnToBudgetManager: string;
    readonly HasExportRule: boolean;
    readonly UserAccesses: SheetForecastFileUserAccess[];

    readonly Custom: Entity<'SheetForecastFileReport'>['Custom'] & {
        readonly update: NetworkSynchronizable;
        readonly import: NetworkSynchronizable;
    };
}

export interface SheetForecastReport extends Entity<'SheetForecastReport'> {
    readonly Name: string;
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly OwnerID: number;
    readonly StartDate: Date | null;
    readonly EndDate: Date | null;
    readonly RollingPeriod: boolean;
    readonly RollingHistoryMonths: number | null;
    readonly IsBudget: boolean;
    readonly AggregateAutomatically: boolean;

    readonly Files: SheetForecastFileReport[];
}

export interface ForecastModelDataEntryType extends SystemEnumerationEntity<'ForecastModelDataEntryType', enumerations.ForecastModelDataEntryType> { }

export interface ForecastModelDataEntryStatus extends SystemEnumerationEntity<'ForecastModelDataEntryStatus', enumerations.ForecastModelDataEntryStatus> { }

export interface ForecastModelDataEntry extends Entity<'ForecastModelDataEntry'> {
    readonly TypeID: enumerations.ForecastModelDataEntryType;
    readonly LevelID: number;
    readonly Name: string;
    readonly IndicatorGroupID: enumerations.PerformanceIndicatorGroup;
    readonly CustomIndicatorIDs: number[];
    readonly ResponsibleUserID: number;
    readonly IsPrivate: boolean;
    readonly StatusID: enumerations.ForecastModelDataEntryStatus;
    readonly ManagerNote: string;
    readonly ResponsibleUserNote: string;
    readonly LastReminderSent: Date | null;
    readonly LastUpdate: Date | null;
    readonly LastError: Date | null;
    readonly LastErrorMessage: string;
    readonly GoogleConfigurationID: number | null;
    readonly SheetName: string;
    readonly StatusSheetColumn: string;
    readonly ManagerNoteSheetColumn: string;
    readonly ResponsibleUserNoteSheetColumn: string;
    readonly ExcelConfigurationID: number | null;
    readonly Custom: Entity<'ForecastModelDataEntry'>['Custom'] & {
        readonly template: FileTransferHelperEntity & { ValueWriteRuleID: enumerations.ExcelGenerationValueWriteRule | null; };
        readonly import: FileTransferHelperEntity;
    };
}

export interface ForecastModelAggregationReport extends Entity<'ForecastModelAggregationReport'> {
    readonly IndicatorID: number;
    readonly Date: Date;
    readonly CurrentTotal: number;
    readonly AggregatedTotal: number;
}

export interface ForecastModelAggregationLevelReport extends Entity<'ForecastModelAggregationLevelReport'> {
    readonly ModelID: number;
    readonly FatherID: number | null;
    readonly ChildrenTrackingLevelID: enumerations.TrackingLevel | null;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly LastUpdate: Date | null;
    readonly Entries: ForecastModelDataEntry[];
    readonly Custom: Entity<'ForecastModelAggregationLevelReport'>['Custom'] & {
        readonly subdivisionItemIDs: number[];
        readonly aggregation: NetworkArray<ForecastModelAggregationReport>;
    };
}

export interface ForecastModelRecordDataActualSplitRange extends Entity<'ForecastModelRecordDataActualSplitRange'> {
    readonly FileID: number;
    readonly FromAccountNumber: number;
    readonly ToAccountNumber: number;
}

export interface ForecastModelRecordDataFileReport extends UserTrackingEntity<'ForecastModelRecordDataFileReport'> {
    readonly RecordID: number;
    readonly SheetExcelConfigurationID: number | null;
    readonly LocalFileName: string;
    readonly ActualSplitRanges: ForecastModelRecordDataActualSplitRange[];
}

export interface ForecastModelRecordDataTransferLog extends Entity<'ForecastModelRecordDataTransferLog'> {
    readonly RecordID: number;
    readonly Message: string;
}

export interface ForecastModelRecordDataImportPeriodType extends SystemEnumerationEntity<'ForecastModelRecordDataImportPeriodType', enumerations.ForecastModelRecordDataImportPeriodType> { }

export interface ForecastModelRecordDataReport extends UserTrackingEntity<'ForecastModelRecordDataReport'> {
    readonly Name: string;
    readonly LastRunTime: Date | null;
    readonly ImportPeriodTypeID: enumerations.ForecastModelRecordDataImportPeriodType;
    readonly ImportAfterCustomPeriod: Date | null;
    readonly StopOnEmptyRow: boolean;
    readonly ImportActual: boolean;
    readonly ImportBudget: boolean;
    readonly ImportForecast: boolean;
    readonly SetLastConfirmedFinancialPeriod: boolean;
    readonly NegateImportedCostAccountNumbers: boolean;

    readonly Files: ForecastModelRecordDataFileReport[];
    readonly LogMessages: ForecastModelRecordDataTransferLog[];
    readonly Custom: Entity<'ForecastModelRecordDataReport'>['Custom'] & {
        readonly import: NetworkSynchronizable;
        readonly results: NetworkPaginationSlice<AccountingData>;
    };
}

export interface ForecastModelReport extends UserTrackingEntity<'ForecastModelReport'> {
    readonly Name: string;
    readonly StartDate: Date | null;
    readonly EndDate: Date | null;
    readonly RollingPeriod: boolean;
    readonly RollingHistoryMonths: number | null;
    readonly DimensionID: enumerations.PerformanceIndicatorDimension;
    readonly AutoAggregation: boolean;
    readonly OwnerID: number;
    readonly Levels: ForecastModelAggregationLevelReport[];
    readonly Custom: Entity<'ForecastModelAggregationLevelReport'>['Custom'] & {
        readonly hasChildrenTrackingLevelID: boolean;
        readonly childrenTrackingLevelID: enumerations.TrackingLevel | null;
        readonly aggregation: NetworkArray<ForecastModelAggregationReport>;
    };
}

export interface MeetingReport extends Entity<'MeetingReport'> {
    readonly TemplateID: number | null;
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly OrganizerID: number;
    readonly StatusID: number;
    readonly StartDate: Date;
    readonly EndDate: Date | null;
    readonly Title: string;
    readonly FunctionID: number;
    readonly InvitesSendDate: Date;
    readonly InvitesSent: boolean;
    readonly BlobStorageURI: string;
    readonly ArchivedBy: number | null;
    readonly ArchivedDate: Date;
    readonly CustomMinutesBlobStorageURI: string;
    readonly StartFromLastFollowUps: boolean;
    readonly FollowUpsSectionDuration: number;
    readonly ReminderSendDate: Date | null;

    readonly Sections: NetworkPaginationSlice<MeetingSectionReport>;
    readonly Participants: MeetingParticipant[];

    readonly Custom: Entity<'MeetingReport'>['Custom'] & {
        readonly invitationStatus: enumerations.RequestStatus;
    };
}

export interface MeetingParticipant extends Entity<'MeetingParticipant'> {
    readonly MeetingID: number;
    readonly UserID: number;
    readonly InviteSent: Date | null;
    readonly MeetingLastSeen: Date | null;
}

export interface MeetingSectionContentType extends SystemEnumerationEntity<'MeetingSectionContentType', enumerations.MeetingSectionContentType> {}

export interface MeetingSectionReport extends Entity<'MeetingSectionReport'> {
    readonly MeetingID: number;
    readonly OrganizerID: number;
    readonly Name: string;
    readonly BlobStorageURI: string;
    readonly Duration: number;
    readonly SectionTopics: MeetingSectionTopicReport[];
}

export interface MeetingSectionTopicReport extends Entity<'MeetingSectionTopicReport'> {
    readonly SectionID: number;
    readonly ContentTypeID: enumerations.MeetingSectionContentType;
    readonly Notes: string;
    readonly TextTopic: MeetingSectionTextTopic;
    readonly TopicLink: MeetingSectionTopicLink;
    readonly Attachments: MeetingSectionTopicAttachment[];
}

export interface MeetingSectionTextTopic extends Entity<'MeetingSectionTextTopic'> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly SectionTopicID: number;
    readonly Name: string;
    readonly Description: string;
}

export interface MeetingSectionTopicLink extends Entity<'MeetingSectionTopicLink'> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly SectionTopicID: number;
    readonly ContentTypeID: number;
    readonly ContentID: number;
    readonly ForwardText: string;
    readonly EmbeddedToMeetingSectionTopic: boolean;
}

export interface MeetingSectionTopicAttachment extends Entity<'MeetingSectionTopicAttachment'> {
    readonly SectionTopicID: number;
    readonly FileName: string;
    readonly MimeType: string;
    readonly BlobStorageURI: string;
    readonly Embed: boolean;
    readonly EmbeddedToDiscussion: boolean;
    readonly IsImage: boolean;
}

export interface MeetingTemplateParticipant extends Entity<'MeetingTemplateParticipant'> {
    readonly TemplateID: number;
    readonly UserID: number;
}

export interface MeetingTemplateAdministrator extends Entity<'MeetingTemplateAdministrator'> {
    readonly TemplateID: number;
    readonly UserID: number;
}

interface MeetingTemplate<ET extends string = 'MeetingTemplate'> extends Entity<ET> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly OrganizerID: number;
    readonly Title: string;
    readonly FunctionID: number;
    readonly InvitationOffset: number;
    readonly InvitationPeriodTypeID: number;
    readonly LastCreatedMeeting: Date | null;
    readonly StartFromLastFollowUps: boolean;
    readonly FollowUpsSectionDuration: number;
    readonly ReminderSendDate: Date | null;
    readonly PromoteInAgendaHome: boolean;
}

export interface MeetingTemplateReport extends MeetingTemplate<'MeetingTemplateReport'> {
    readonly Participants: MeetingTemplateParticipant[];
    readonly Administrators: MeetingTemplateAdministrator[];
    readonly Sections: MeetingTemplateSectionReport[];
    readonly AgendaThemeID: number | null;

    readonly Custom: Entity<'MeetingTemplateReport'>['Custom'] & {
        readonly promoteMeetingCreation?: boolean;
    };
}

export interface MeetingTemplateSectionReport extends Entity<'MeetingTemplateSectionReport'> {
    readonly TemplateID: number;
    readonly OrganizerID: number;
    readonly Name: string;
    readonly BlobStorageURI: string;
    readonly Duration: number;
    readonly SectionTopics: MeetingTemplateSectionTopicReport[];
}

export interface MeetingTemplateSectionTopicReport extends Entity<'MeetingTemplateSectionTopicReport'> {
    readonly SectionID: number;
    readonly ContentTypeID: enumerations.MeetingSectionContentType;
    readonly Notes: string;
    readonly TextTopic: MeetingTemplateSectionTextTopic;
    readonly TopicLink: MeetingTemplateSectionTopicLink;
}

export interface MeetingTemplateSectionTextTopic extends Entity<'MeetingTemplateSectionTextTopic'> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly SectionTopicID: number;
    readonly Name: string;
    readonly Description: string;
}

export interface MeetingTemplateSectionTopicLink extends Entity<'MeetingTemplateSectionTopicLink'> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly SectionTopicID: number;
    readonly ContentTypeID: number;
    readonly ContentID: number;
    readonly ForwardText: string;
    readonly EmbeddedToMeetingSectionTopic: boolean;
}

export interface MeetingInvitationPeriodType extends SystemEnumerationEntity<'MeetingInvitationPeriodType', enumerations.MeetingInvitationPeriodType> { }
export interface MeetingStatus extends SystemEnumerationEntity<'MeetingStatus', enumerations.MeetingStatus> { }
export interface MeetingMinutesContentType extends SystemEnumerationEntity<'MeetingMinutesContentType', enumerations.MeetingMinutesContentType> { }

export interface PerformanceIndicatorPollSettingsLightReport extends Entity<'PerformanceIndicatorPollSettingsLightReport'> {
    readonly IndicatorID: number;
    readonly CreatorUserID: number;
    readonly ValueAggregationTypeID: enumerations.PerformanceIndicatorPollValueAggregationType;
    readonly AggregateByFunction: number;
    readonly TargetUserFunctionID: number | null;
    readonly TargetUserID: number | null;
    readonly ValueTypeID: enumerations.PerformanceIndicatorPollValueType;
    readonly DimensionID: enumerations.PerformanceIndicatorDimension;
    readonly IntroductionText: string;
    readonly ScheduleStartDate: Date;
    readonly Name: string;
    readonly PeriodID: enumerations.PeriodType;
    readonly NotificationSendingTimeTypeID: enumerations.NotificationSendingTimeType;
    readonly PerformanceIndicatorPeriodTypeID: enumerations.PeriodType;
    readonly CollectionTypeID: enumerations.PerformanceIndicatorCollectionType | null;
    readonly AnonymizeResults: boolean;
    readonly SendToGenericRecipients: boolean;
    readonly GenericRecipientAddresses: string;
    readonly SendToMicrosoftTeamsRecipients: boolean;
    readonly StatusID: enumerations.PerformanceIndicatorPollStatus;
    readonly ResponsibleUserID: number;
    readonly AccessibleByManagers: boolean;
    readonly CurrentValue: number | null;
    readonly CurrentPeriodCommentsCount: number;
    readonly CurrentPeriodAnswersCount: number;
    readonly CurrentPeriodTotalAnswers: number;
    readonly CurrentPeriodStart: Date;
    readonly CurrentPeriodEnd: Date;
    readonly NextBatchDate: Date;
    readonly LatestBatchDateWithUsers: Date | null;
    readonly Batches: NetworkPaginationSlice<PerformanceIndicatorPollBatch>;
}

export interface PerformanceIndicatorPollBatch extends Entity<'PerformanceIndicatorPollBatch'> {
    readonly SettingsID: number;
    readonly BatchCreated: Date;
}

export interface PerformanceIndicatorPollBatchUserReport extends Entity<'PerformanceIndicatorPollBatchUserReport'> {
    readonly PollBatchID: number;
    readonly UserID: number;
    readonly DateResponded: Date | null;
    readonly ResponseValue: number | null;
    readonly Comment: string;
    readonly BatchCreated: Date;
}

export interface PollBatchUserPaginationSlice extends NetworkPaginationSlice<PerformanceIndicatorPollBatchUserReport> {
    readonly Custom: PerformanceIndicatorPollBatchUserReport['Custom'] & {
        readonly loadedPeriods: number[];
    };
}

export interface PerformanceIndicatorPollGeneralResponseReport extends Entity<'PerformanceIndicatorPollGeneralResponseReport'> {
    readonly PollBatchID: number;
    readonly CompanyFunctionID: number;
    readonly DateResponded: Date | null;
    readonly ResponseValue: number | null;
    readonly Comment: string;
    readonly MicrosoftTeamsMemberID: string;
    readonly BatchCreated: Date;
}

export interface PollBatchGeneralAnswerPaginationSlice extends NetworkPaginationSlice<PerformanceIndicatorPollGeneralResponseReport> {
    readonly Custom: PerformanceIndicatorPollGeneralResponseReport['Custom'] & {
        readonly loadedPeriods: number[];
    };
}

export interface PollSettingsScale extends Entity<'PollSettingsScale'> {
    readonly PollID: number;
    readonly Value: number;
    readonly Title: string;
}

export interface FileTransferHelperEntity extends Entity<'FileTransferHelperEntity'> {
    readonly Name: string;
    readonly URL: string;
}

export interface IntegrationDataMappingDimension extends SystemEnumerationEntity<'IntegrationDataMappingDimension', enumerations.IntegrationDataMappingDimension> { }

export interface ERPCorporateAccount extends Entity<'ERPCorporateAccount'> {
    readonly Name: string;
    readonly AccountNumber: number;
}

export interface PerformanceIndicatorAccountNumberArray extends NetworkObject<number[]> {
    readonly Custom: NetworkObject<number[]>['Custom'] & {
        readonly indicatorID: number;
    };
}

export interface AccountingDataPreviewReport extends Entity<'AccountingDataPreviewReport'> {
    readonly AccountNumber: number;
    readonly Debet: number;
    readonly Kredit: number;
    readonly Description: string;
    readonly Dimension1: string;
    readonly Dimension2: string;
    readonly Dimension3: string;
    readonly Dimension4: string;
    readonly BookingDate: Date;
}

export interface InvoicingDataPreviewReport extends Entity<'InvoicingDataPreviewReport'> {
    readonly InvoiceNumber: string;
    readonly InvoicingCustomerName: string;
    readonly ProductSyncGroupName: string;
    readonly CustomerSyncGroupName: string;
    readonly Dimension1: string;
    readonly Dimension2: string;
    readonly Dimension3: string;
    readonly InvoiceDate: Date;
    readonly TotalNetSum: number;
    readonly ProductCode: string;
    readonly ProductName: string;
    readonly AccountingSuggestion: string;
    readonly ProductLineNetSum: number;
    readonly SellerIdentifier: string;
}

export interface AccountingData extends Entity<'AccountingData'> {
    readonly ExternalID: string;
    readonly AccountNumber: number;
    readonly Debet: number;
    readonly Kredit: number;
    readonly Description: string;
    readonly Dimension1: string;
    readonly Dimension2: string;
    readonly Dimension3: string;
    readonly Dimension4: string;
    readonly Dimension5: string;
    readonly Dimension6: string;
    readonly BookingDate: Date | null;
    readonly SegmentID: number | null;
    readonly ExternalSalesInvoiceID: number | null;
    readonly DebetCumulative: number | null;
    readonly KreditCumulative: number | null;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly TransactionTypeID: number;
    readonly TargetCorporateLegalEntityID: number | null;
    readonly ForecastModelRecordDataID: number | null;
}

export interface ERPFinancialAdvisorySettings extends Entity<'ERPFinancialAdvisorySettings'> {
    readonly CheckPeriodDataDuringDay: boolean;
    readonly UpdateFinancialInformationWithDataChange: boolean;
    readonly SynchronizeBillingInformation: boolean;
}

export interface NewsEntrySourceReport extends Entity<'NewsEntrySourceReport'> {
    readonly LastCheckDate: Date;
    readonly Entries: NetworkPaginationSlice<Entity>;
}

export interface StrategyHealthReport extends Entity<'StrategyHealthReport'> {
    readonly Title: string;
    readonly Icon: string;
    readonly StatusPercentage: number;
    readonly StatusMessage: string;
    readonly DetailedStatusMessage: string;
}

export interface GoogleOAuthUser extends Entity<'GoogleOAuthUser'> {
    readonly LinkedToUserID: number;
    readonly GoogleEmail: string;
    readonly AccessToken: string;
    readonly IsPublic: number;
}

export interface SheetConfiguration extends Entity<'SheetConfiguration'> {
    readonly OwnerID: number;
    readonly CreatedDate: Date;
    readonly LastFetchTimestamp: Date | null;
    readonly LastFetchError: string;
    readonly SheetName: string;
    readonly SheetExcelID: number | null;
    readonly SheetGoogleID: number | null;
    readonly PerformanceIndicatorID: number | null;
    readonly DataRange: string;
    readonly TimePeriodRange: string;
    readonly DimensionID: enumerations.PerformanceIndicatorDimension;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly PeriodID: enumerations.PeriodType;
    readonly ForecastDownloadFromSheetRuleID: enumerations.SheetConfigurationForecastDownloadFromSheetRule;
    readonly ForecastUploadToSheetRuleID: enumerations.SheetConfigurationForecastUploadToSheetRule;
}

export interface SheetExcelConfiguration extends Entity<'SheetExcelConfiguration'> {
    readonly Custom: Entity<'SheetExcelConfiguration'>['Custom'] & {
        readonly isAuthenticated: NetworkObject<boolean>;
    };
    readonly Name: string;
    readonly TemplateFileBlobStorageURI: string;
    readonly MicrosoftDriveFileID: string;
    readonly IsValidFile: boolean;
    readonly MicrosoftDriveURL: string;
    readonly MicrosoftDriveID: string;
    readonly MicrosoftShareURL: string;
    readonly LocalFileName: string;
}

export interface SheetGoogleConfiguration extends Entity<'SheetGoogleConfiguration'> {
    readonly Custom: Entity<'SheetGoogleConfiguration'>['Custom'] & {
        readonly isAuthenticated: NetworkObject<boolean>;
    };
    readonly SpreadsheetName: string;
    readonly SpreadsheetID: string;
    readonly GoogleOAuthUserID: number | null;
    readonly ForecastTemplateSourceIdentifier: string;
    readonly IsGeneratedSheet: boolean;
    readonly FileWatchID: string;
    readonly IsRolling: boolean;
    readonly SendReminder: boolean;
    readonly RemindedUserID: number | null;
    readonly ReminderPeriodTypeID: enumerations.PeriodType | null;
    readonly IsTemplate: boolean;
}

export interface SheetGoogleTemplateCopiedRangeValueType extends SystemEnumerationEntity<'SheetGoogleTemplateCopiedRangeValueType', enumerations.SheetGoogleTemplateCopiedRangeValueType> { }

export interface SheetGoogleTemplateCopiedRangeSettings {
    readonly SourceTemplateID: number;
    readonly GoogleConfigurationIDs: number[];
    readonly Ranges: SheetGoogleTemplateCopiedRange[];
}

export interface SheetGoogleTemplateCopiedRange {
    readonly ValueTypeID: enumerations.SheetGoogleTemplateCopiedRangeValueType;
    readonly StartCell: string;
    readonly EndCell: string;
}

export interface NotificationReport extends Entity<'NotificationReport'> {
    readonly UserID: number | null;
    readonly TypeID: enumerations.NotificationType;
    readonly Icon: string;
    readonly TimeStamp: Date;
    readonly ContentTemplate: string;
    readonly ContentVariables: { [k: string]: string; };
    readonly DetailsURL: string;
    readonly DetailsURLParameters: { [k: string]: string; };
    readonly Description: string;
}

export interface NotificationReportPaginationSlice extends NetworkPaginationSlice<NotificationReport> {
    readonly NewCount: number;
    readonly Custom: NetworkPaginationSlice<NotificationReport>['Custom'] & {
        readonly visibleNewCount: number;
        readonly touchStatus: enumerations.RequestStatus;
    };
}

export interface NotificationFilter {
    readonly TypeIDs: enumerations.NotificationType[];
    readonly StakeholderIDs: number[];
    readonly RecipientUserIDs: number[];
    readonly TextFilter: string;
    readonly ExcludeOwn: boolean;
    readonly ExcludeUserID: number | null;
    readonly StartDate: Date | null;
    readonly EndDate: Date | null;
}

export interface NotificationFeed {
    readonly filter: Partial<NotificationFilter>;
    readonly notifications: NotificationReportPaginationSlice;
    readonly Custom: NotificationReportPaginationSlice['Custom'];
}

export interface ExcelShareURLTest extends NetworkObject<boolean> {
    readonly ShareURL: string;
}

export interface CompanySubscriptionReport extends Entity<'CompanySubscriptionReport'> {
    readonly ProductPackageID: enumerations.ProductPackage;
    readonly CreationChannelID: enumerations.CompanyCreationChannel;
    readonly CreationChannelName: string;
    readonly ServiceLevels: CompanySubscriptionServiceLevel[];
    readonly Users: CompanyUserServiceLevelBase[];
}

export interface CompanySubscriptionServiceLevel extends Entity<'CompanySubscriptionServiceLevel'> {
    readonly Description: string;
    readonly FlavorText: string;
    readonly ImageURL: string;
    readonly UserMonthlyPrice: number;
    readonly IsActive: boolean;
}

export interface CompanyUserServiceLevelBase extends Entity<'CompanyUserServiceLevelBase'> {
    readonly UserID: number;
    readonly ServiceLevelID: number | null;
}

export interface BusinessModelReport extends UserTrackingEntity<'BusinessModelReport'> {
    readonly Name: string;
    readonly CanvasItems: BusinessModelCanvasItem[];
}

export interface BusinessModelCanvasArea extends SystemEnumerationEntity<'BusinessModelCanvasArea', enumerations.BusinessModelCanvasArea> {
    readonly Icon: string;
    readonly DefaultName: string;
    readonly NameFI: string;
    readonly NameEN: string;
    readonly NameSE: string;
    readonly Name: string;
    readonly DefaultDescription: string;
    readonly DescriptionFI: string;
    readonly DescriptionEN: string;
    readonly DescriptionSE: string;
    readonly Description: string;
    readonly CompactViewX: number;
    readonly CompactViewY: number;
    readonly CompactViewWidth: number;
    readonly CompactViewHeight: number;
    readonly FullViewX: number;
    readonly FullViewY: number;
    readonly FullViewWidth: number;
    readonly FullViewHeight: number;
}

export interface BusinessModelCanvasItem extends Entity<'BusinessModelCanvasItem'> {
    readonly ModelID: number;
    readonly AreaID: enumerations.BusinessModelCanvasArea;
    readonly Name: string;
    readonly TypeID: enumerations.BusinessModelCanvasItemType;
    readonly DashboardBlockID: number | null;
    readonly StakeholderBenchmarkID: number | null;
    readonly StakeholderMarketPositioningMapViewID: number | null;
    readonly Description: string;
}

export interface BusinessModelCanvasItemType extends SystemEnumerationEntity<'BusinessModelCanvasItemType', enumerations.BusinessModelCanvasItemType> {}

export interface StrategicGoalMilestoneStatus extends SystemEnumerationEntity<'StrategicGoalMilestoneStatus', enumerations.StrategicGoalMilestoneStatus> {}
export interface StrategicGoalMilestoneFinalStatus extends SystemEnumerationEntity<'StrategicGoalMilestoneFinalStatus', enumerations.StrategicGoalMilestoneFinalStatus> {}
export interface StrategicGoalMilestoneAchievability extends SystemEnumerationEntity<'StrategicGoalMilestoneAchievability', enumerations.StrategicGoalMilestoneAchievability> {
    readonly Icon: string;
}
export interface StrategyDescriptionStatus extends SystemEnumerationEntity<'StrategyDescriptionStatus', enumerations.StrategyDescriptionStatus> {}
export interface StrategyPublishingType extends SystemEnumerationEntity<'StrategyPublishingType', enumerations.StrategyPublishingType> {}
export interface StrategyNamingType extends SystemEnumerationEntity<'StrategyNamingType', enumerations.StrategyNamingType> {}

export interface BingCognitiveItem extends Entity<'BingCognitiveItem'> {
    readonly Keywords: string;
    readonly Name: string;
    readonly ImageURI: string;
    readonly Url: string;
    readonly Description: string;
    readonly Provider: string;
    readonly Published: Date;
}

export interface StakeholderNewsPreviewArray extends NetworkArray<BingCognitiveItem> {
    readonly Custom: NetworkArray<BingCognitiveItem>['Custom'] & {
        readonly brandNames: string;
        readonly includeEnglishMarkets: boolean;
        readonly newsSearch: boolean;
        readonly typeID: number;
        readonly stakeholderID: number;
        readonly strictnessLevel: enumerations.StakeholderNewsWatchFilterLevel;
        readonly webSearch: boolean;
    };
}

export interface BusinessReviewStatus extends SystemEnumerationEntity<'BusinessReviewStatus', enumerations.BusinessReviewStatus> { }

export interface BusinessReviewSessionStatus extends SystemEnumerationEntity<'BusinessReviewSessionStatus', enumerations.BusinessReviewSessionStatus> {
    readonly ColorHex: string;
}

export interface BusinessReviewSession extends Entity<'BusinessReviewSession'> {
    readonly ScheduleID: number;
    readonly TemplateID: number;
    readonly TargetUserID: number;
    readonly InvitationDate: Date;
    readonly DueDate: Date;
    readonly DateStarted: Date;
    readonly DateClosed: Date | null;
    readonly Comment: string;
    readonly StatusID: enumerations.BusinessReviewStatus;
    readonly SessionEndStatusID: enumerations.BusinessReviewSessionStatus | null;
}

export interface BusinessReviewStep extends SortableEntity<'BusinessReviewStep'> {
    readonly TemplateID: number;
    readonly Name: string;
    readonly TargetPageURI: string;
    readonly Sort: number;
}

export interface BusinessReviewTemplateReport extends Entity<'BusinessReviewTemplateReport'> {
    readonly Name: string;
    readonly CreatorID: number;
    readonly UserID: number;
    readonly Introduction: string;
    readonly Description: string;
    readonly DateAdded: Date;
    readonly ShowMinimizeButton: boolean;
    readonly DimmedBackground: boolean;
    readonly WindowSizeID: number;
    readonly HideStepSelection: boolean;
    readonly Sessions: NetworkPaginationSlice<BusinessReviewSession>;
    readonly Steps: BusinessReviewStep[];
}

export interface BusinessReviewSchedule extends Entity<'BusinessReviewSchedule'> {
    readonly Name: string;
    readonly CreatorID: number;
    readonly DateAdded: Date;
    readonly TargetUserID: number;
    readonly InvitationDate: Date;
    readonly DueDate: Date;
}

export interface ERPDimensionSyncSetting extends Entity<'ERPDimensionSyncSetting'> {
    readonly SourceTrackingLevelID: enumerations.TrackingLevel;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface ERPAdminCostDivisionType extends SystemEnumerationEntity<'ERPAdminCostDivisionType', enumerations.ERPAdminCostDivisionType> {}

export interface ERPAdminCostDivisionRuleSourceFilter extends Entity<'ERPAdminCostDivisionRuleSourceFilter'> {
    readonly DivisionRuleID: number;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface ERPAdminCostDivisionRuleFromAccount extends Entity<'ERPAdminCostDivisionRuleFromAccount'> {
    readonly RuleID: number;
    readonly FromAccountNumber: number;
    readonly ToAccountNumber: number | null;
}

export interface ERPAdminCostDivisionRuleFixedRule extends Entity<'ERPAdminCostDivisionRuleFixedRule'> {
    readonly RuleID: number;
    readonly Amount: number;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface ERPAdminCostDivisionRuleLevel extends Entity<'ERPAdminCostDivisionRuleLevel'> {
    readonly RuleID: number;
    readonly IsActive: boolean;
    readonly CorporateStructureLink: CorporateStructureLink;
}

export interface ERPAdminCostDivisionRuleReport extends SortableEntity<'ERPAdminCostDivisionRuleReport'> {
    readonly Created: Date;
    readonly CreatorID: number;
    readonly Name: string;
    readonly DivisionMethodTypeID: enumerations.ERPAdminCostDivisionType;
    readonly DivisionMethodAccountRange: string;
    readonly DivisionMethodIndicatorID: number | null;
    readonly SourceTrackingLevelID: enumerations.TrackingLevel;
    readonly TargetAccountNumber: number | null;
    readonly ValidFromDate: Date | null;
    readonly ValidToDate: Date | null;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly SourceFilter: ERPAdminCostDivisionRuleSourceFilter;
    readonly FromAccounts: ERPAdminCostDivisionRuleFromAccount[];
    readonly FixedRules: ERPAdminCostDivisionRuleFixedRule[];
    readonly Levels: ERPAdminCostDivisionRuleLevel[];
}

export interface CompanyVirtualTeam extends Entity<'CompanyVirtualTeam'> {
    readonly Name: string;

    readonly Custom: Entity<'CompanyVirtualTeam'>['Custom'] & {
        journeyStartDate: Date | null;
        journeyEndDate: Date | null;
    };
}

export interface CompanyVirtualTeamMember extends Entity<'CompanyVirtualTeamMember'> {
    readonly TeamID: number;
    readonly UserID: number;
}

export interface PerformanceIndicatorCalculationRange extends SystemEnumerationEntity<'PerformanceIndicatorCalculationRange', enumerations.PerformanceIndicatorCalculationRange> {}

export interface ERPFinancialCalculationError extends Entity<'ERPFinancialCalculationError'> {
    readonly Created: Date;
    readonly ErrorMessage: string;
}

export interface PowerBIToken extends Entity<'PowerBIToken'> {
    readonly UserID: number;
    readonly LastUpdateDate: Date | null;
}

export interface PowerBIItem extends NetworkEntity {
    readonly Identifier: string;
    readonly Name: string;
    readonly EmbedURL: string;
}

export interface PowerBIWorkspace extends NetworkEntity {
    readonly Identifier: string;
    readonly Name: string;
    readonly Dashboards: PowerBIItem[];
    readonly Reports: PowerBIItem[];
}

export interface PowerBIWorkspaceNetworkArray extends NetworkSynchronizable {
    readonly Data: PowerBIWorkspace[];
}

export interface PollReport extends Entity<'PollReport'> {
    readonly PollCategory: number;
    readonly PollIdentifier: number;
    readonly Title: string;
    readonly PollTitle: string;
    readonly CreatedDateTime: Date;
    readonly TargetPeriodID: enumerations.PeriodType;
    readonly IntroductionText: string;
    readonly IntroductionImageText: string;
    readonly Questions: Entity[];
    readonly IndicatorID: number;
}

export interface WorkshopCloneSettings {
    readonly SourceWorkshopID: number;
    readonly UseTemplateCompany: boolean;
    readonly Name: string;
    readonly CloneParticipants: boolean;
    readonly CloneComments: boolean;
    readonly CloneItems: boolean;
    readonly CloneRatings: boolean;
    readonly TargetCompanyIDs: number[];
    readonly ParticipantIDs: number[];
}

export interface WorkshopVideoCall extends Entity<'WorkshopVideoCall'> {
    readonly WorkshopID: number;
    readonly RoomIdentifier: string;
    readonly HostRoomUrl: string;
    readonly ParticipantRoomUrl: string;
    readonly StartAutomatically: boolean;
    readonly IsWebinar: boolean;
    readonly RoomExpirationDate: Date;
}

interface Workshop<ET extends string = 'Workshop'> extends Entity<ET> {
    readonly Created: Date;
    readonly CreatorID: number;
    readonly OwnerID: number | null;
    readonly TemplateID: number | null;
    readonly MilestoneID: number | null;
    readonly Name: string;
    readonly IsTemplate: boolean;
    readonly IsDemo: boolean;
    readonly DoesFacilitatorParticipate: boolean;
    readonly ResultSummary: string;
    readonly MarketID: number | null;
    readonly StrategicGoalID: number | null;
    readonly PreviewBlobStorageUrl: string;
    readonly DefaultPreviewBlobStorageUrl: string;
    readonly IsContinuous: boolean;
    readonly CustomBackgroundImageBlobStorageUrl: string;
    readonly PromoteInAgendaHome: boolean;
}

export interface WorkshopReport extends Workshop<'WorkshopReport'> {
    readonly Custom: UserTrackingEntity<'WorkshopReport'>['Custom'] & {
        readonly visited: boolean;
        readonly phaseStatus: enumerations.RequestStatus;
        readonly cloningStatus: enumerations.RequestStatus;
        readonly exportStatus: enumerations.RequestStatus;
        readonly templateConversionStatus: enumerations.RequestStatus;
        readonly versions: number[];
        readonly version: number | null;
    };

    readonly IsCompleted: boolean;
    readonly AreResultsFilterable: boolean;
    readonly ParticipantRoles: WorkshopParticipantRole[];
    readonly Participants: WorkshopParticipant[];
    readonly Phases: WorkshopPhaseReport[];
    readonly ContentRows: NetworkArray<WorkshopPhaseContentRowReport>;
    readonly ItemHistory: NetworkArray<WorkshopItemHistory>;
    readonly ItemRatingHistory: NetworkArray<WorkshopItemRatingHistory>;
    readonly VideoCall: WorkshopVideoCall | null;
}

export interface WorkshopBackgroundImage extends SystemEnumerationEntity<'WorkshopBackgroundImage', enumerations.WorkshopBackgroundImage> {
    readonly Custom: SystemEnumerationEntity<'WorkshopBackgroundImage', enumerations.WorkshopBackgroundImage>['Custom'] & {
        base64Image: '';
    };
    readonly BlobStorageUrl: string;
}

export interface WorkshopParticipantRole extends Entity<'WorkshopParticipantRole'> {
    readonly WorkshopID: number;
    readonly Name: string;
}

export interface WorkshopParticipant extends Entity<'WorkshopParticipant'> {
    readonly WorkshopID: number;
    readonly UserID: number | null;
    readonly InviteSent: Date | null;
    readonly LastVisit: Date | null;
    readonly FirstVisit: Date | null;
    readonly UserName: string;
    readonly RoleID: number | null;
    readonly Email: string;
}

export interface WorkshopPhaseContentCreationRule extends SystemEnumerationEntity<'WorkshopPhaseContentCreationRule', enumerations.WorkshopPhaseContentCreationRule> {
    IsCategoryCreationRule: boolean;
    IsItemCreationRule: boolean;
}

export interface WorkshopCanvasItemType extends SystemEnumerationEntity<'WorkshopCanvasItemType', enumerations.WorkshopCanvasItemType> {}

export interface WorkshopPhaseReport extends SortableEntity<'WorkshopPhaseReport'> {
    readonly WorkshopID: number;
    readonly Name: string;
    readonly HasDeadline: boolean;
    readonly DeadlineStartDate: Date | null;
    readonly DeadlineEndDate: Date | null;
    readonly IsPublished: boolean;
    readonly PausedAt: Date | null;
    readonly ResultSummary: string;
    readonly TemplateDefaultPublicationStatus: boolean;
    readonly Created: Date;
    readonly CustomBackgroundImageBlobStorageUrl: string;
    readonly IsClosed: boolean;

    readonly ParticipantRoles: WorkshopPhaseParticipantRole[];
    readonly CanvasItems: WorkshopCanvasItem[];
}

export interface WorkshopPhaseParticipantRole extends Entity<'WorkshopPhaseParticipantRole'> {
    readonly PhaseID: number;
    readonly ParticipantRoleID: number;
}

export interface WorkshopPhaseContentRowLayout extends SystemEnumerationEntity<'WorkshopPhaseContentRowLayout', enumerations.WorkshopPhaseContentRowLayout> {}

export interface WorkshopPhaseContentRowReport extends SortableEntity<'WorkshopPhaseContentRowReport'> {
    readonly PhaseID: number;
    readonly LayoutID: enumerations.WorkshopPhaseContentRowLayout;

    readonly Widgets: WorkshopWidgetReport[];
}

export interface WorkshopWidgetType extends SystemEnumerationEntity<'WorkshopSystemEnumeration', enumerations.WorkshopWidgetType> {
    readonly HasItems: boolean;
    readonly HasRatings: boolean;
    readonly Icon: string;
    readonly Sort: number;
}

export interface WorkshopWidgetStatus extends SystemEnumerationEntity<'WorkshopWidgetStatus', enumerations.WorkshopWidgetStatus> {}
export interface WorkshopExternalContentType extends SystemEnumerationEntity<'WorkshopExternalContentType', enumerations.WorkshopExternalContentType> {}

interface WorkshopWidget<ET extends string = 'WorkshopWidget'> extends Entity<ET> {
    readonly Created: Date;
    readonly CreatorID: number;
    readonly ContentRowID: number;
    readonly Name: string;
    readonly Description: string;
    readonly TypeID: enumerations.WorkshopWidgetType;
    readonly StatusID: enumerations.WorkshopWidgetStatus | null;
    readonly TemplateDefaultStatusID: enumerations.WorkshopWidgetStatus | null;
    readonly RowPosition: number;
    readonly CategoryCreationRuleID: enumerations.WorkshopPhaseContentCreationRule | null;
    readonly CategoryCreationWidgetID: number | null;
    readonly ItemCategoryConversionLimit: number | null;
    readonly ItemCreationRuleID: enumerations.WorkshopPhaseContentCreationRule | null;
    readonly FromCrossPollinationWidgetID: number | null;
    readonly ToCrossPollinationWidgetID: number | null;
    readonly MinimumIdentifiedItemsCount: number | null;
    readonly XAxisName: string;
    readonly YAxisName: string;
    readonly AxisStartPointExplanation: string;
    readonly AxisEndPointExplanation: string;
    readonly UseGridLayout: boolean;
    readonly VotingTypeID: enumerations.WorkshopPrioritizationVotingType | null;
    readonly CustomVoteCount: number | null;
    readonly DescendingRatedItemOrder: boolean;
    readonly AllowPersonalItemRating: boolean;
    readonly LastResultCalculationDate: Date;
    readonly AskTrackingLevel: boolean;
    readonly TargetTrackingLevelID: enumerations.TrackingLevel | null;
    readonly AllowIndicatorImport: boolean;
    readonly ResultPresentationTypeID: enumerations.WorkshopResultPresentationType | null;
    readonly ExternalContentUrl: string;
    readonly EmbedExternalContent: boolean;
    readonly ExternalContentTypeID: enumerations.WorkshopExternalContentType | null;
    readonly ExternalContentMimeType: string;
    readonly ExternalContentPreviewImageUrl: string;
    readonly IdeaGenerationIntro: string;
    readonly IdeaGenerationPrompt: string;
    readonly DefaultExportStrategyID: number | null;
    readonly DefaultExportGoalID: number | null;
    readonly DefaultExportMilestoneID: number | null;
    readonly DefaultExportProgramID: number | null;
}

export interface WorkshopWidgetReport extends WorkshopWidget<'WorkshopWidgetReport'> {
    readonly Custom: WorkshopWidget<'WorkshopWidgetReport'>['Custom'] & {
        readonly CartesianGraphFilter?: {
            readonly ShowPersonalAnswers?: boolean;
            readonly ParticipantRoleIDs?: number[];
            readonly ColorBySpread?: boolean;
            readonly ExcludedCategoryIDs?: (number | null)[];
            readonly ExcludedSpreadLevels?: number[];
        };
        readonly ComparisonResultView?: enumerations.WorkshopComparisonResultView;
        readonly CompactBusinessModelCanvas?: boolean;
        readonly Ideas?: NetworkObject<string[]>;
    };

    readonly ParticipantStatuses: WorkshopWidgetParticipantStatus[];
    readonly ItemCategories: WorkshopItemCategory[];
    readonly ItemCategoryConnections: WorkshopItemCategoryConnection[];
    readonly Items: WorkshopItem[];
    readonly RatingPhaseSources: WorkshopRatingPhaseSourceReport[];
    readonly ItemRatings: WorkshopItemRating[];
    readonly GridBlocks: WorkshopPhaseGridBlock[];
    readonly ComparisonAxes: WorkshopPhaseComparisonAxis[];
    readonly ComparisonDimensions: WorkshopPhaseComparisonDimension[];
    readonly QuestionnaireFilters: WorkshopQuestionnaireFilter[];
    readonly QuestionnaireQuestions: WorkshopQuestionnaireQuestionReport[];
    readonly ResultPhaseSources: WorkshopResultPhaseSource[];
    readonly IdeaGenerationThemes: WorkshopWidgetIdeaGenerationTheme[];
}

export interface WorkshopWidgetParticipantStatus extends Entity<'WorkshopWidgetParticipantStatus'> {
    readonly WidgetID: number;
    readonly ParticipantID: number;
    readonly Completed: Date;
}

export interface WorkshopPrioritizationVotingType extends SystemEnumerationEntity<'WorkshopPrioritizationVotingType', enumerations.WorkshopPrioritizationVotingType> {}

export interface WorkshopQuestionnaireFilter extends Entity<'WorkshopQuestionnaireFilter'> {
    readonly ParticipantID: number;
    readonly CorporateStructureLink: CorporateStructureLink;
    readonly WidgetID: number;
}

export interface WorkshopQuestionnaireQuestionType extends SystemEnumerationEntity<'WorkshopQuestionnaireQuestionType', enumerations.WorkshopQuestionnaireQuestionType> {
    readonly Icon: string;
}

export interface WorkshopQuestionnaireQuestionReport extends SortableEntity<'WorkshopQuestionnaireQuestionReport'> {
    readonly Question: string;
    readonly QuestionTypeID: enumerations.WorkshopQuestionnaireQuestionType;
    readonly IsMandatory: boolean;
    readonly PerformanceIndicatorID: number | null;
    readonly WidgetID: number;

    readonly Options: WorkshopQuestionnaireOption[];
    readonly Responses: WorkshopQuestionnaireResponse[];
}

export interface WorkshopQuestionnaireOption extends SortableEntity<'WorkshopQuestionnaireOption'> {
    readonly QuestionID: number;
    readonly Name: string;
    readonly OptionWeight: number | null;
}

export interface WorkshopQuestionnaireResponse extends UserTrackingEntity<'WorkshopQuestionnaireResponse'> {
    readonly QuestionID: number;
    readonly OptionID: number | null;
    readonly TextResponse: string;
    readonly ParticipantID: number | null;
    readonly QuestionnaireFilterID: number | null;
}

export interface WorkshopCanvasItem extends UserTrackingEntity<'WorkshopCanvasItem'> {
    readonly ParticipantID: number | null;
    readonly PhaseID: number;
    readonly Text: string;
    readonly ItemTypeID: enumerations.WorkshopCanvasItemType;
    readonly GroupID: number | null;
    readonly X: number;
    readonly Y: number;
    readonly Width: number;
    readonly Height: number;
}

export interface WorkshopItemCategory extends SortableEntity<'WorkshopItemCategory'> {
    readonly Name: string;
    readonly IsSubcategory: boolean;
    readonly CardColor: string;
    readonly WidgetID: number;
}

export interface WorkshopItemCategoryConnection extends Entity<'WorkshopItemCategoryConnection'> {
    readonly ParentCategoryID: number;
    readonly SourceCategoryID: number | null;
    readonly LastCheckDate: Date | null;
    readonly SourceWidgetID: number;
    readonly SourceWorkshopID: number;
}

export interface WorkshopItem extends UserTrackingEntity<'WorkshopItem'> {
    readonly CategoryID: number | null;
    readonly Name: string;
    readonly Description: string;
    readonly MergedToItemID: number | null;
    readonly FromCrossPollinationItemID: number | null;
    readonly ToCrossPollinationItemID: number | null;
    readonly Sort: number | null;
    readonly ParticipantID: number | null;
    readonly SubcategoryID: number | null;
    readonly UpdatedBy: number;
    readonly WidgetID: number;

    readonly Custom: Entity<'WorkshopItem'>['Custom'] & {
        readonly publishTarget?: { elementType: enumerations.StrategySelectElement; entityID: number | null; };
    };
}

export interface WorkshopVersionHistoryChangeType extends SystemEnumerationEntity<'WorkshopVersionHistoryChangeType', enumerations.WorkshopVersionHistoryChangeType> {}

export interface WorkshopItemHistory extends Entity<'WorkshopItemHistory'> {
    readonly Identifier: string;
    readonly ChangeDate: Date;
    readonly ChangeTypeID: enumerations.WorkshopVersionHistoryChangeType;
    readonly ItemID: number;
    readonly Created: Date;
    readonly CreatorID: number;
    readonly ParticipantID: number | null;
    readonly Updated: Date;
    readonly UpdatedBy: number;
    readonly Name: string;
    readonly Description: string;
    readonly CategoryID: number | null;
    readonly SubcategoryID: number | null;
    readonly MergedToItemID: number | null;
    readonly FromCrossPollinationItemID: number | null;
    readonly ToCrossPollinationItemID: number | null;
    readonly Sort: number | null;
    readonly WidgetID: number;
}

export interface WorkshopCanvasItem extends UserTrackingEntity<'WorkshopCanvasItem'> {
    readonly ParticipantID: number | null;
    readonly Text: string;
    readonly IsGroup: boolean;
    readonly IsHint: boolean;
    readonly GroupID: number | null;
    readonly X: number;
    readonly Y: number;
    readonly Width: number;
    readonly Height: number;
    readonly WidgetID: number;
}

export interface WorkshopRatingPhaseWeightedLevel extends SystemEnumerationEntity<'WorkshopRatingPhaseWeightedLevel', enumerations.WorkshopRatingPhaseWeightedLevel> {}

export interface WorkshopRatingPhaseCategoryWeight extends Entity<'WorkshopRatingPhaseCategoryWeight'> {
    readonly RatingPhaseSourceID: number | null;
    readonly ItemCategoryID: number | null;
    readonly Weight: number;
}

export interface WorkshopRatingPhaseSourceReport extends Entity<'WorkshopRatingPhaseSource'> {
    readonly WeightedLevelID: enumerations.WorkshopRatingPhaseWeightedLevel;
    readonly ItemWeight: number;
    readonly RatedItemCount: number | null;
    readonly WidgetID: number;
    readonly RatedWidgetID: number;

    readonly CategoryWeights: WorkshopRatingPhaseCategoryWeight[];
}

export interface WorkshopItemRating extends UserTrackingEntity<'WorkshopItemRating'> {
    readonly ItemID: number;
    readonly Profitability: number;
    readonly Effort: number;
    readonly RelativeProfitability: number | null;
    readonly RelativeEffort: number | null;
    readonly Score: number;
    readonly ComparisonDimensionID: number | null;
    readonly ComparisonRating: number;
    readonly ParticipantID: number | null;
    readonly RelativeScore: number | null;
    readonly Updated: Date;
    readonly WidgetID: number;
}

export interface WorkshopItemRatingHistory extends Entity<'WorkshopItemRatingHistory'> {
    readonly Identifier: string;
    readonly ChangeDate: Date;
    readonly ChangeTypeID: enumerations.WorkshopVersionHistoryChangeType;
    readonly ItemRatingID: number;
    readonly Created: Date;
    readonly CreatorID: number;
    readonly ParticipantID: number | null;
    readonly Updated: Date;
    readonly ItemID: number;
    readonly Profitability: number;
    readonly Effort: number;
    readonly RelativeProfitability: number | null;
    readonly RelativeEffort: number | null;
    readonly Score: number;
    readonly ComparisonDimensionID: number | null;
    readonly ComparisonRating: number;
    readonly RelativeScore: number | null;
    readonly WidgetID: number;
}

export interface WorkshopPhaseGridBlock extends Entity<'WorkshopPhaseGridBlock'> {
    readonly Description: string;
    readonly GridX: number;
    readonly GridY: number;
    readonly BackgroundColor: string;
    readonly Visible: boolean;
    readonly WidgetID: number;
}

export interface WorkshopPhaseComparisonAxis extends SortableEntity<'WorkshopPhaseComparisonAxis'> {
    readonly Name: string;
    readonly WidgetID: number;
}

export interface WorkshopPhaseComparisonDimension extends SortableEntity<'WorkshopPhaseComparisonDimension'> {
    readonly Name: string;
    readonly WidgetID: number;
}

export interface WorkshopResultPresentationType extends SystemEnumerationEntity<'WorkshopResultPresentationType', enumerations.WorkshopResultPresentationType> {}

export interface WorkshopResultPhaseSource extends Entity<'WorkshopResultPhaseSource'> {
    readonly SourceWorkshopID: number;
    readonly Position: number;
    readonly SourceCategoryID: number | null;
    readonly WidgetID: number;
    readonly SourceWidgetID: number;
}

export interface WorkshopWidgetIdeaGenerationTheme extends SortableEntity<'WorkshopWidgetIdeaGenerationTheme'> {
    readonly WidgetID: number;
    readonly Name: string;
    readonly Description: string;
}

export interface WorkshopWidgetIdeaGenerationThemeAnswer {
    readonly ThemeID: number;
    readonly Answer: string;
}

export interface FinancialBatchPreviewReport extends NetworkSynchronizable {
    readonly Errors: string;
    readonly Results: PerformanceIndicatorDataReport[];
    readonly Custom: NetworkSynchronizable['Custom'] & {
        readonly batchID: number;
    };
}

export interface SystemUserRole extends SystemEnumerationEntity<'SystemUserRole', enumerations.SystemUserRole> { }

export interface UserRoleBillingUserType extends SystemEnumerationEntity<'UserRoleBillingUserType', enumerations.UserRoleBillingUserType> {}

export interface AccessModule extends SystemEnumerationEntity<'AccessModule', enumerations.AccessModule> {}

export interface DialogFlowLogicGateMatchRule extends SystemEnumerationEntity<'DialogFlowLogicGateMatchRule', enumerations.DialogFlowLogicGateMatchRule> {
    Sign: string;
}

export interface DialogFlowTemplateMessageOption extends SortableEntity<'DialogFlowTemplateMessageOption'> {
    readonly FlowTemplateID: number;
    readonly FromMessageID: number;
    readonly OptionText: string;
    readonly NextMessageID: number | null;
    readonly IsExitOption: boolean;
    readonly LogicGateMatchRuleID: enumerations.DialogFlowLogicGateMatchRule | null;
}

export interface DialogFlowTemplateMessageType extends SystemEnumerationEntity<'DialogFlowTemplateMessageType', enumerations.DialogFlowTemplateMessageType> {}
export interface DialogFlowTemplateMessageConversionRule extends SystemEnumerationEntity<'DialogFlowTemplateMessageConversionRule', enumerations.DialogFlowTemplateMessageConversionRule> {}
export interface DialogFlowLogicGateValueSource extends SystemEnumerationEntity<'DialogFlowLogicGateValueSource', enumerations.DialogFlowLogicGateValueSource> {}
export interface DialogFlowMultiOptionDataSource extends SystemEnumerationEntity<'DialogFlowMultiOptionDataSource', enumerations.DialogFlowMultiOptionDataSource> {
    readonly PreviewOptions: NetworkObject<{ ID: number; Name: string; }[]>;
}

export interface DialogFlowTemplateMessageReport extends Entity<'DialogFlowTemplateMessageReport'> {
    readonly FlowTemplateID: number;
    readonly MessageTypeID: enumerations.DialogFlowTemplateMessageType;
    readonly Message: string;
    readonly MessageFI: string;
    readonly MessageEN: string;
    readonly MessageSE: string;
    readonly InputMaxLength: number;
    readonly InputVariableName: string;
    readonly ConversionRuleID: enumerations.DialogFlowTemplateMessageConversionRule;
    readonly NextMessageID: number | null;
    readonly LogicGateValueSourceID: enumerations.DialogFlowLogicGateValueSource | null;
    readonly MultiOptionDataSourceID: enumerations.DialogFlowMultiOptionDataSource | null;
    readonly OpenAiResultCollectionPrompt: string;
    readonly CustomMessageTitle: string;
    readonly MaxAnswersCount: number;
    readonly SourceMessageID: number | null;
    readonly TryFilterByStrategy: boolean;

    readonly Options: DialogFlowTemplateMessageOption[];
}

export interface DialogFlowTemplateReport extends UserTrackingEntity<'DialogFlowTemplateReport'> {
    readonly Name: string;
    readonly WelcomeMessage: string;
    readonly EndMessage: string;
    readonly PreviewImageTaken: Date | null;
    readonly PreviewBlobStorageUrl: string;
    readonly DefaultPreviewBlobStorageUrl: string;
    readonly PromoteInAgendaHome: boolean;

    readonly Messages: DialogFlowTemplateMessageReport[];
}

export interface DialogFlowSession extends Entity<'DialogFlowSession'> {
    readonly Created: Date;
    readonly CreatedBy: number;
    readonly FlowTemplateID: number;
    readonly CurrentMessageID: number;
    readonly CreationSourceID: enumerations.DialogFlowSessionCreationSource;
    readonly StartDate: Date | null;
    readonly EndDate: Date | null;
    readonly DialogScheduleBatchID: number | null;
}

export interface StrategyProcessReport extends UserTrackingEntity<'StrategyProcessReport'> {
    readonly Name: string;
    readonly IsActive: boolean;
    readonly Themes: StrategyProcessThemeReport[];
}

export interface StrategyProcessThemeReport extends SortableEntity<'StrategyProcessThemeReport'> {
    readonly StrategyProcessID: number;
    readonly Name: string;
    readonly Subthemes: StrategyProcessSubthemeReport[];
}

export interface StrategyProcessSubthemeType extends SystemEnumerationEntity<'StrategyProcessSubthemeType', enumerations.StrategyProcessSubthemeType> {}

export interface StrategyProcessSubthemeReport extends Entity<'StrategyProcessSubthemeReport'> {
    readonly ThemeID: number;
    readonly Name: string;
    readonly Description: string;
    readonly TypeID: enumerations.StrategyProcessSubthemeType;
    readonly WorkshopID: number | null;
    readonly MeetingTemplateID: number | null;
    readonly ExternalURL: string;
    readonly FileName: string;
    readonly MimeType: string;
    readonly BlobStorageURL: string;
    readonly Links: StrategyProcessSubthemeLink[];
}

export interface StrategyProcessSubthemeLink extends Entity<'StrategyProcessSubthemeLink'> {
    readonly FromSubthemeID: number;
    readonly ToSubthemeID: number;
}

export interface StrategyProcessPhaseReport extends Entity<'StrategyProcessPhaseReport'> {
    readonly SwimLaneTitle: string;
    readonly Title: string;
    readonly StartDate: Date;
    readonly EndDate: Date;
    readonly PhaseContents: StrategyProcessPhaseContent[];
}

export interface StrategyProcessPhaseContent extends Entity<'StrategyProcessPhaseContent'> {
    readonly PhaseID: number;
    readonly Title: string;
    readonly WorkshopID: number | null;
    readonly MeetingID: number | null;
    readonly ExternalURL: string;
}

export interface UserFunctionalityAccessReport extends Entity<'UserFunctionalityAccessReport'> {
    readonly FunctionalityID: number;
    readonly AccessTypeID: number;
}

export interface SpecialMenuItem extends Entity<'SpecialMenuItem'> {
    readonly FunctionalityURL: string;
    readonly ItemName: string;
}

export interface SystemUserType extends SystemEnumerationEntity<'SystemUserType', enumerations.SystemUserType> {}

export interface StrategySavedView extends UserTrackingEntity<'StrategySavedView'> {
    readonly Name: string;
    readonly FilterJson: string;
    readonly ContentSettingsJson: string;
    readonly MapFilterJson: string;
    readonly OrganizationStructureFilterJson: string;
    readonly IsPublic: boolean;
}

export interface UserNotificationEventSource extends SystemEnumerationEntity<'UserNotificationEventSource', enumerations.UserNotificationEventSource> {
    readonly SendToEmail: boolean;
    readonly SendToMobile: boolean;
    readonly Parameter1: number | null;
}

export interface UserNotificationUserSettings extends Entity<'UserNotificationUserSettings'> {
    readonly UserID: number;
    readonly SourceID: enumerations.UserNotificationEventSource;
    readonly SendEmail: boolean;
    readonly SendMobile: boolean;
    readonly Parameter1: number | null;
}

export interface UserCompanyFunctionalityAccess extends Entity<'UserCompanyFunctionalityAccess'> {
    readonly UserID: number;
    readonly FunctionalityID: enumerations.AccessFunctionality;
    readonly AccessTypeID: enumerations.AccessType;
}

export interface DialogScheduleRhytm extends SystemEnumerationEntity<'DialogScheduleRhytm', enumerations.DialogScheduleRhytm> {}
export interface DialogDistributionChannel extends SystemEnumerationEntity<'DialogDistributionChannel', enumerations.DialogDistributionChannel> {}

export interface DialogScheduleFlow extends SortableEntity<'DialogScheduleFlow'> {
    readonly DialogScheduleID: number;
    readonly DialogFlowTemplateID: number;
}

export interface DialogScheduleUser extends Entity<'DialogScheduleUser'> {
    readonly DialogScheduleID: number;
    readonly UserID: number;
}

export interface DialogScheduleBatchReport extends UserTrackingEntity<'DialogScheduleBatchReport'> {
    readonly DialogScheduleID: number;
    readonly StartDate: Date;
    readonly EndDate: Date;
    readonly IsSent: boolean;
    readonly IsClosed: boolean;
    readonly ResultWorkshopID: number | null;
    readonly Name: string;
    readonly AgendaThemeID: number | null;
    readonly StrategyDescriptionID: number | null;
    readonly Description: string;
    readonly SendDate: Date | null;

    readonly Sessions: DialogFlowSession[];
}

export interface DialogScheduleSettingsReport extends UserTrackingEntity<'DialogScheduleSettingsReport'> {
    readonly ScheduleRhytmID: enumerations.DialogScheduleRhytm;
    readonly Name: string;
    readonly IsActive: boolean;
    readonly StartDate: Date;
    readonly PeriodDaysOpen: number;
    readonly UseScheduling: boolean;
    readonly MicrosoftTeamsBotConversationID: number | null;
    readonly DistributionChannelID: enumerations.DialogDistributionChannel;
    readonly IsFeedbackDialog: boolean;
    readonly StrategyDescriptionID: number | null;

    readonly LatestBatchStartDate: Date | null;
    readonly Flows: DialogScheduleFlow[];
    readonly Users: DialogScheduleUser[];
    readonly Batches: DialogScheduleBatchReport[];
}

export interface DashboardPageBlockLightReport extends Entity<'DashboardPageBlockLightReport'> {
    readonly Name: string;
    readonly PageID: number | null;
    readonly TypeID: enumerations.DashboardBlockType;
}

export interface DashboardPageLightReport extends Entity<'DashboardPageLightReport'> {
    readonly Name: string;
    readonly GroupID: number | null;
    readonly IsPrivate: boolean;
    readonly Blocks: DashboardPageBlockLightReport[];
}

export interface DashboardGroupLightReport extends Entity<'DashboardGroupLightReport'> {
    readonly Name: string;

    readonly Pages: DashboardPageLightReport[];
}

export interface WorkspaceMindmapNodeType extends Entity<'WorkspaceMindmapNodeType'> {
    readonly MindmapID: number;
    readonly Name: string;
    readonly Color: string;
    readonly StrategyElementTypeID: enumerations.StrategyElementType | null;
}

export interface WorkspaceMindmapNode extends UserTrackingEntity<'WorkspaceMindmapNode'> {
    readonly MindmapID: number;
    readonly Name: string;
    readonly NodeTypeID: number;
    readonly FatherID: number | null;
    readonly Description: string;

    readonly Custom: Entity<'WorkspaceMindmapNode'>['Custom'] & {
        readonly publishTarget?: { elementType: enumerations.StrategySelectElement; entityID: number | null; };
    };
}

export interface WorkspaceMindmapReport extends UserTrackingEntity<'WorkspaceMindmapReport'> {
    readonly Name: string;
    readonly PreviewImageTakenAt: Date | null;
    readonly PreviewBlobStorageUrl: string;
    readonly IsActive: boolean;
    readonly WorkshopWidgetID: number | null;

    readonly WorkshopID: number | null;
    readonly NodeTypes: WorkspaceMindmapNodeType[];
    readonly Nodes: NetworkPaginationSlice<WorkspaceMindmapNode>;
}

export interface AnalysisCategory extends UserTrackingEntity<'AnalysisCategory'> {
    readonly Name: string;
    readonly Description: string;
    readonly FatherID: number | null;
}

export interface AgendaTheme extends UserTrackingEntity<'AgendaTheme'> {
    readonly Name: string;
    readonly Sort: number;
}

export interface AgendaTimeReservation extends UserTrackingEntity<'AgendaTimeReservation'> {
    readonly Name: string;
    readonly AgendaThemeID: number | null;
    readonly StartDate: Date;
    readonly EndDate: Date;
    readonly TemplateTableName: string;
    readonly TemplateID: number | null;
    readonly ConversionInProgress: boolean;
}

export interface AgendaFlag extends UserTrackingEntity<'AgendaFlag'> {
    readonly Name: string;
    readonly AgendaThemeID: number | null;
    readonly TargetDate: Date;
}

export interface AgendaTimeSerieRuleReport extends UserTrackingEntity<'AgendaTimeSerieRuleReport'> {
    readonly IntervalPeriodTypeID: enumerations.PeriodType;
    readonly IntervalPeriodDay: enumerations.DayOfWeek;
    readonly IntervalStartDate: Date;
    readonly ItemCreationDayOffset: number;
    readonly NotifyCreator: boolean;

    readonly MeetingTemplateID: number | null;
    readonly TimeReservationID: number | null;
    readonly PollID: number | null;
    readonly WorkshopID: number | null;
    readonly Events: AgendaTimeSerieEvent[];
}

export interface AgendaTimeSerieEvent extends Entity<'AgendaTimeSerieEvent'> {
    readonly Name: string;
    readonly SerieRuleID: number;
    readonly StartDate: Date;
    readonly EndDate: Date;
    readonly TargetEntityID: number | null;
}

export interface CompanyAccessModule extends Entity<'CompanyAccessModule'> {
    readonly ModuleID: enumerations.AccessModule;
    readonly StartDate: Date | null;
    readonly EndDate: Date | null;
    readonly ActivatedBy: number;
    readonly DiscountPercentage: number | null;
}

export interface MicrosoftTeamsBotLinkedConversation extends Entity<'MicrosoftTeamsBotLinkedConversation'> {
    readonly TeamName: string;
    readonly ConversationName: string;
}

export interface UserInterfaceTranslationLanguage extends Entity<'UserInterfaceTranslationLanguage'> {
    readonly Identifier: string;
    readonly LanguageCode: string;
    readonly Description: string;
}

export interface WorkshopStrategyImportSettings {
    readonly CategorySource: enumerations.WorkshopStrategyImportSource;
    readonly CategoryEntityIDs: number[];
    readonly ItemSource: enumerations.WorkshopStrategyImportSource;
    readonly ItemEntityIDs: number[];
}

export interface CompanyAccessFunctionalityAddOn extends Entity<'CompanyAccessFunctionalityAddOn'> {
    readonly AddOnID: enumerations.AccessFunctionalityAddOn;
    readonly StartTime: Date;
}

export interface AccessFunctionalityAddOn extends SystemEnumerationEntity<'AccessFunctionalityAddOn', enumerations.AccessFunctionalityAddOn> {
    readonly Price: number;
    readonly IsSmartStrategyAddOn: boolean;
}

import defaults from 'api/defaults';

export default defaults as {
    NetworkObject: NetworkObject<void>;
    NetworkArray: NetworkArray<Entity>;
    NetworkPaginationSlice: NetworkPaginationSlice<Entity>;
    NestedNetworkPaginationSlice: NestedNetworkPaginationSlice<Entity>;
    NetworkObjectOf: <T>(defaultValue: T) => NetworkObject<T>;
    NetworkArrayOf: <T extends Entity>(defaultValue: T) => NetworkArray<T>;
    NetworkPaginationSliceOf: <T extends Entity>(defaultValue: T) => NetworkPaginationSlice<T>;
    NestedNetworkPaginationSliceOf: <T extends Entity>(defaultValue: T) => NestedNetworkPaginationSlice<T>;
    EntityChange: EntityChange;
    Session: Session;
    SystemLanguage: SystemLanguage;
    GeneralSettings: GeneralSettings;
    PublicExternalLinkType: PublicExternalLinkType;
    PublicExternalLink: PublicExternalLink;
    LastFinancialPeriodType: LastFinancialPeriodType;
    Company: Company;
    UserCompanyAccess: UserCompanyAccess;
    PartnerSupportLevel: PartnerSupportLevel;
    PartnerCompany: PartnerCompany;
    PartnerClientCompanyType: PartnerClientCompanyType;
    PartnerClientCompanyReport: PartnerClientCompanyReport;
    PartnerClientCompanyEventType: PartnerClientCompanyEventType;
    PartnerClientCompanyEvent: PartnerClientCompanyEvent;
    CorporateStructureLink: CorporateStructureLink;
    CorporateStructureFilter: CorporateStructureFilter;
    PerformanceManagementAlertType: PerformanceManagementAlertType;
    PerformanceManagementAlertActivityStatus: PerformanceManagementAlertActivityStatus;
    PerformanceManagementAlertTriggerType: PerformanceManagementAlertTriggerType;
    PerformanceManagementAlertRecipient: PerformanceManagementAlertRecipient;
    PerformanceManagementAlertTriggerIndicator: PerformanceManagementAlertTriggerIndicator;
    PerformanceManagementAlertIndicator: PerformanceManagementAlertIndicator;
    PerformanceManagementAlertReviewSectionComment: PerformanceManagementAlertReviewSectionComment;
    PerformanceManagementAlertReviewSectionReport: PerformanceManagementAlertReviewSectionReport;
    PerformanceManagementAlertDashboardBlockComment: PerformanceManagementAlertDashboardBlockComment;
    PerformanceManagementAlertDashboardBlockReport: PerformanceManagementAlertDashboardBlockReport;
    PerformanceManagementAlertReport: PerformanceManagementAlertReport;
    PerformanceManagementAlertScore: PerformanceManagementAlertScore;
    PerformanceManagementAlertEmailTitleType: PerformanceManagementAlertEmailTitleType;
    PerformanceIndicatorGroupReport: PerformanceIndicatorGroupReport;
    PerformanceIndicatorFormula: PerformanceIndicatorFormula;
    PerformanceIndicatorReport: PerformanceIndicatorReport;
    PerformanceIndicatorDataFilterRange: PerformanceIndicatorDataFilterRange;
    PerformanceIndicatorDataFilter: PerformanceIndicatorDataFilter;
    PerformanceIndicatorDataBase: PerformanceIndicatorDataBase;
    PerformanceIndicatorDataReport: PerformanceIndicatorDataReport;
    PerformanceIndicatorAvailabilityReport: PerformanceIndicatorAvailabilityReport;
    Country: Country;
    MarketScopeType: MarketScopeType;
    StakeholderNewsWatchFilterLevel: StakeholderNewsWatchFilterLevel;
    NewsEntryAutomatedSource: NewsEntryAutomatedSource;
    StakeholderNewsEntryAutomatedSource: StakeholderNewsEntryAutomatedSource;
    StakeholderComment: StakeholderComment;
    StakeholderReport: StakeholderReport;
    StakeholderTypeReport: StakeholderTypeReport;
    StakeholderMarketPositioningMapViewPosition: StakeholderMarketPositioningMapViewPosition;
    StakeholderMarketPositioningMapViewReport: StakeholderMarketPositioningMapViewReport;
    StakeholderMarketPositioningMapViewTemplate: StakeholderMarketPositioningMapViewTemplate;
    StakeholderBenchmarkDimensionValue: StakeholderBenchmarkDimensionValue;
    StakeholderBenchmarkDimensionReport: StakeholderBenchmarkDimensionReport;
    StakeholderBenchmarkReport: StakeholderBenchmarkReport;
    StakeholderBenchmarkTemplate: StakeholderBenchmarkTemplate;
    CorporateStructureSortMode: CorporateStructureSortMode;
    TrackingLevel: TrackingLevel;
    Product: Product;
    GeoArea: GeoArea;
    Customer: Customer;
    TrackingLevelItemBase: TrackingLevelItemBase;
    TrackingLevel1Item: TrackingLevel1Item;
    TrackingLevel2Item: TrackingLevel2Item;
    TrackingLevel3Item: TrackingLevel3Item;
    TrackingLevel4Item: TrackingLevel4Item;
    CompanyLegalEntity: CompanyLegalEntity;
    CompanyFunction: CompanyFunction;
    NewsEntrySettingsBase: NewsEntrySettingsBase;
    NewsEntryFilter: NewsEntryFilter;
    NewsEntryComment: NewsEntryComment;
    FinancialIndicator: FinancialIndicator;
    FinancialIndicatorDataFilterRange: FinancialIndicatorDataFilterRange;
    FinancialIndicatorDataFilter: FinancialIndicatorDataFilter;
    FinancialIndicatorDataBase: FinancialIndicatorDataBase;
    FinancialIndicatorDataReport: FinancialIndicatorDataReport;
    ERPSyncProductMonthData: ERPSyncItemMonthData;
    ERPSyncCustomerMonthData: ERPSyncItemMonthData;
    ERPSyncProductReport: ERPSyncProductReport;
    ERPSyncCustomerReport: ERPSyncCustomerReport;
    ERPSyncProductReportERPSyncItemPaginationSlice: ERPSyncItemPaginationSlice<ERPSyncProductReport>;
    ERPSyncCustomerReportERPSyncItemPaginationSlice: ERPSyncItemPaginationSlice<ERPSyncCustomerReport>;
    ForesightScenarioReport: ForesightScenarioReport;
    ForesightScenarioNotificationRecipient: ForesightScenarioNotificationRecipient;
    ForesightScenarioLevelReport: ForesightScenarioLevelReport;
    ForesightScenarioElementType: ForesightScenarioElementType;
    ForesightScenarioBaselineCalculation: ForesightScenarioBaselineCalculation;
    ForesightScenarioElementReport: ForesightScenarioElementReport;
    ForesightScenarioFactor: ForesightScenarioFactor;
    BusinessSimulatorCashflowAccountReceivablesDistribution: BusinessSimulatorCashflowAccountReceivablesDistribution;
    BusinessSimulatorCashflowSalesReceivablesDistribution: BusinessSimulatorCashflowSalesReceivablesDistribution;
    BusinessSimulatorCashflowCapitalItemGroup: BusinessSimulatorCashflowCapitalItemGroup;
    BusinessSimulatorCashflowCapitalItem: BusinessSimulatorCashflowCapitalItem;
    BusinessSimulatorCashflowCapitalItemValue: BusinessSimulatorCashflowCapitalItemValue;
    GoalMetricValueType: GoalMetricValueType;
    GoalMetricValueSource: GoalMetricValueSource;
    GoalMetricReport: GoalMetricReport;
    StrategyProgressCalculationMethod: StrategyProgressCalculationMethod;
    StrategyProgressCalculationSource: StrategyProgressCalculationSource;
    StrategyExecutionSecondaryStatusUpdateType: StrategyExecutionSecondaryStatusUpdateType;
    StrategicProgramStatus: StrategicProgramStatus;
    StrategicProgramFinalStatus: StrategicProgramFinalStatus;
    StrategicProgramUpdateFrequency: StrategicProgramUpdateFrequency;
    StrategicProgramRiskLevel: StrategicProgramRiskLevel;
    StrategicProgramStatusUpdateReport: StrategicProgramStatusUpdateReport;
    StrategicProgramStatusUpdateRecipient: StrategicProgramStatusUpdateRecipient;
    StrategicProgramComment: StrategicProgramComment;
    StrategicProgramSecondaryOwner: StrategicProgramSecondaryOwner;
    StrategicProgramReport: StrategicProgramReport;
    StrategicGoalReport: StrategicGoalReport;
    StrategicGoalCriticalSuccessFactorReport: StrategicGoalCriticalSuccessFactorReport;
    StrategyTaxonomy: StrategyTaxonomy;
    PerformancePerspectiveGroup: PerformancePerspectiveGroup;
    StrategyDescriptionReport: StrategyDescriptionReport;
    StrategyDescriptionParticipator: StrategyDescriptionParticipator;
    StrategyDescriptionAdministrator: StrategyDescriptionAdministrator;
    StrategyDescriptionPerformanceIndicator: StrategyDescriptionPerformanceIndicator;
    StrategyPageBlock: StrategyPageBlock;
    StrategyPageMemoBlock: StrategyPageMemoBlock;
    DashboardPageBlockType: DashboardPageBlockType;
    PerformanceIndicatorDimension: PerformanceIndicatorDimension;
    PerformanceIndicatorComparisonTarget: PerformanceIndicatorComparisonTarget;
    PerformanceIndicatorComparisonCalculation: PerformanceIndicatorComparisonCalculation;
    PerformanceIndicatorConversation: PerformanceIndicatorConversation;
    PerformanceIndicatorConversationReport: PerformanceIndicatorConversationReport;
    StrategicProgramConnection: StrategicProgramConnection;
    StrategyFreeNodeReport: StrategyFreeNodeReport;
    StrategyPageBlockType: StrategyPageBlockType;
    IntegrationSource: IntegrationSource;
    IntegrationSourceCompanyStatus: IntegrationSourceCompanyStatus;
    SheetGenerationSettings: SheetGenerationSettings;
    SheetGenerationSettingsIndicator: SheetGenerationSettingsIndicator;
    UserLocal: UserLocal;
    DecisionPollReport: DecisionPollReport;
    DecisionPollRespondent: DecisionPollRespondent;
    DecisionPollReplyOptionTypeReport: DecisionPollReplyOptionTypeReport;
    DecisionTopicReport: DecisionTopicReport;
    DecisionTopicComment: DecisionTopicComment;
    StrategicGoalMilestoneReport: StrategicGoalMilestoneReport;
    SWOTInbox: SWOTInbox;
    StrategicGoalMilestoneComment: StrategicGoalMilestoneComment;
    DashboardPageTemplate: DashboardPageTemplate;
    DashboardGroup: DashboardGroup;
    DashboardPageHomeSettings: DashboardPageHomeSettings;
    DashboardPageReport: DashboardPageReport;
    DashboardPageUserAccess: DashboardPageUserAccess;
    DashboardPageBlockTemplate: DashboardPageBlockTemplate;
    DashboardPageBlockTableSerieRowReport: DashboardPageBlockTableSerieRowReport | HackedDashboardPageBlockTableSerieRowReport;
    DashboardPageBlockTableComparisonRow: DashboardPageBlockTableComparisonRow;
    FinancialsAndDecisionMakers: FinancialsAndDecisionMakers;
    IntegrationFinancialUploadBatchFileReport: IntegrationFinancialUploadBatchFileReport;
    IntegrationFinancialUploadBatchExcelFile: IntegrationFinancialUploadBatchExcelFile;
    DecisionTopicType: DecisionTopicType;
    DecisionTopicPhase: DecisionTopicPhase;
    SheetExcelGenerationIndicatorDepthRule: SheetExcelGenerationIndicatorDepthRule;
    SheetExcelGenerationValueWriteRule: SheetExcelGenerationValueWriteRule;
    SheetExcelGenerationSettings: SheetExcelGenerationSettings;
    ERPDimensionMapping: ERPDimensionMapping;
    IntegrationDataMappingRule: IntegrationDataMappingRule;
    IntegrationDataMappingMatchRule: IntegrationDataMappingMatchRule;
    IntegrationDataMappingSource: IntegrationDataMappingSource;
    ERPFivaldiIntegrationSettings: ERPFivaldiIntegrationSettings;
    ERPNetvisorIntegrationSettings: ERPNetvisorIntegrationSettings;
    ERPNetvisorIntegrationLockPeriod: ERPNetvisorIntegrationLockPeriod;
    ERPProcountorIntegrationSettings: ERPProcountorIntegrationSettings;
    CategoryReport: CategoryReport;
    InsightReport: InsightReport;
    InsightLinkReport: InsightLinkReport;
    InsightImage: InsightImage;
    InsightTaggedUser: InsightTaggedUser;
    InsightReactionLink: InsightReactionLink;
    InsightReactionType: InsightReactionType;
    InsightType: InsightType;
    InsightFilter: InsightFilter;
    InsightNetworkArray: InsightNetworkArray;
    InsightDefaultExcludedDomain: InsightDefaultExcludedDomain;
    IntegrationFinancialUploadBatch: IntegrationFinancialUploadBatch;
    DecisionReport: DecisionReport;
    DecisionTrackingType: DecisionTrackingType;
    SheetForecastReport: SheetForecastReport;
    ForecastModelDataEntryType: ForecastModelDataEntryType;
    ForecastModelDataEntryStatus: ForecastModelDataEntryStatus;
    ForecastModelReport: ForecastModelReport;
    ForecastModelAggregationLevelReport: ForecastModelAggregationLevelReport;
    ForecastModelDataEntry: ForecastModelDataEntry;
    ForecastModelRecordDataReport: ForecastModelRecordDataReport;
    ForecastModelRecordDataImportPeriodType: ForecastModelRecordDataImportPeriodType;
    ForecastModelRecordDataFileReport: ForecastModelRecordDataFileReport;
    ForecastModelRecordDataTransferLog: ForecastModelRecordDataTransferLog;
    ForecastModelRecordDataActualSplitRange: ForecastModelRecordDataActualSplitRange;
    ForecastModelAggregationReport: ForecastModelAggregationReport;
    MeetingReport: MeetingReport;
    MeetingParticipant: MeetingParticipant;
    MeetingSectionContentType: MeetingSectionContentType;
    MeetingSectionReport: MeetingSectionReport;
    MeetingSectionTopicAttachment: MeetingSectionTopicAttachment;
    MeetingSectionTopicReport: MeetingSectionTopicReport;
    MeetingSectionTopicLink: MeetingSectionTopicLink;
    MeetingTemplate: MeetingTemplate;
    MeetingTemplateReport: MeetingTemplateReport;
    MeetingTemplateParticipant: MeetingTemplateParticipant;
    MeetingStatus: MeetingStatus;
    PerformanceIndicatorPollSettingsLightReport: PerformanceIndicatorPollSettingsLightReport;
    PerformanceIndicatorPollBatchUserReport: PerformanceIndicatorPollBatchUserReport;
    PollSettingsScale: PollSettingsScale;
    DashboardPageBlockReport: DashboardPageBlockReport;
    DashboardPageBlockTableRollingComparison: DashboardPageBlockTableRollingComparison;
    IntegrationDataMappingDimension: IntegrationDataMappingDimension;
    ERPCorporateAccount: ERPCorporateAccount;
    PerformanceIndicatorAccountNumberArray: PerformanceIndicatorAccountNumberArray;
    AccountingDataPreviewReport: AccountingDataPreviewReport;
    InvoicingDataPreviewReport: InvoicingDataPreviewReport;
    AccountingData: AccountingData;
    VATPeriodType: VATPeriodType;
    CashflowForecastSettingsReport: CashflowForecastSettingsReport;
    CashflowForecastIndicatorConnectionReport: CashflowForecastIndicatorConnectionReport;
    CashflowForecastIndicatorDistributionEntry: CashflowForecastIndicatorDistributionEntry;
    CashflowForecastDataReport: CashflowForecastDataReport;
    GoogleOAuthUser: GoogleOAuthUser;
    SheetConfiguration: SheetConfiguration;
    SheetExcelConfiguration: SheetExcelConfiguration;
    SheetGoogleConfiguration: SheetGoogleConfiguration;
    SheetGoogleTemplateCopiedRangeSettings: SheetGoogleTemplateCopiedRangeSettings;
    SheetGoogleTemplateCopiedRange: SheetGoogleTemplateCopiedRange;
    NotificationReport: NotificationReport;
    NotificationReportPaginationSlice: NotificationReportPaginationSlice;
    NotificationFilter: NotificationFilter;
    CompanySubscriptionReport: CompanySubscriptionReport;
    BusinessModelCanvasArea: BusinessModelCanvasArea;
    BusinessModelReport: BusinessModelReport;
    BusinessModelCanvasItem: BusinessModelCanvasItem;
    BusinessModelCanvasItemType: BusinessModelCanvasItemType;
    CompanyUserServiceLevelBase: CompanyUserServiceLevelBase;
    StakeholderBenchmarkCustomItem: StakeholderBenchmarkCustomItem;
    StakeholderMarketPositioningMapViewCustomItem: StakeholderMarketPositioningMapViewCustomItem;
    StrategicGoalMilestoneStatusUpdate: StrategicGoalMilestoneStatusUpdate;
    StrategicGoalMilestoneSecondaryStatusUpdate: StrategicGoalMilestoneSecondaryStatusUpdate;
    StrategyDescriptionStatus: StrategyDescriptionStatus;
    StrategyPublishingType: StrategyPublishingType;
    StrategyNamingType: StrategyNamingType;
    StrategicGoalMilestoneStatus: StrategicGoalMilestoneStatus;
    StrategicGoalMilestoneFinalStatus: StrategicGoalMilestoneFinalStatus;
    StrategicGoalMilestoneAchievability: StrategicGoalMilestoneAchievability;
    StrategyHealthReport: StrategyHealthReport;
    DashboardPageBlockComment: DashboardPageBlockComment;
    DashboardPageBlockTableSumColumn: DashboardPageBlockTableSumColumn;
    StakeholderNewsPreviewArray: StakeholderNewsPreviewArray;
    BusinessReviewSession: BusinessReviewSession;
    BusinessReviewStep: BusinessReviewStep;
    BusinessReviewTemplateReport: BusinessReviewTemplateReport;
    BusinessReviewSchedule: BusinessReviewSchedule;
    BusinessReviewSessionStatus: BusinessReviewSessionStatus;
    ERPDimensionSyncSetting: ERPDimensionSyncSetting;
    ERPAdminCostDivisionType: ERPAdminCostDivisionType;
    ERPAdminCostDivisionRuleReport: ERPAdminCostDivisionRuleReport;
    ERPAdminCostDivisionRuleSourceFilter: ERPAdminCostDivisionRuleSourceFilter;
    ERPAdminCostDivisionRuleFromAccount: ERPAdminCostDivisionRuleFromAccount;
    ERPAdminCostDivisionRuleFixedRule: ERPAdminCostDivisionRuleFixedRule;
    ERPAdminCostDivisionRuleLevel: ERPAdminCostDivisionRuleLevel;
    CompanyVirtualTeam: CompanyVirtualTeam;
    CompanyVirtualTeamMember: CompanyVirtualTeamMember;
    PerformanceIndicatorValueDependencyRule: PerformanceIndicatorValueDependencyRule;
    PerformanceIndicatorPeriodType: PerformanceIndicatorPeriodType;
    PerformanceIndicatorCalculationRange: PerformanceIndicatorCalculationRange;
    ERPFinancialCalculationError: ERPFinancialCalculationError;
    PowerBIToken: PowerBIToken;
    PowerBIEmbedToken: PowerBIEmbedToken;
    DashboardPageBlockPowerBIEmbed: DashboardPageBlockPowerBIEmbed;
    DashboardPageBlockStrategy: DashboardPageBlockStrategy;
    PowerBIItem: PowerBIItem;
    PowerBIWorkspace: PowerBIWorkspace;
    PowerBIWorkspaceNetworkArray: PowerBIWorkspaceNetworkArray;
    PollReport: PollReport;
    WorkshopCloneSettings: WorkshopCloneSettings;
    Workshop: Workshop;
    WorkshopReport: WorkshopReport;
    WorkshopBackgroundImage: WorkshopBackgroundImage;
    WorkshopParticipantRole: WorkshopParticipantRole;
    WorkshopParticipant: WorkshopParticipant;
    WorkshopVideoCall: WorkshopVideoCall;
    WorkshopPhaseContentCreationRule: WorkshopPhaseContentCreationRule;
    WorkshopCanvasItemType: WorkshopCanvasItemType;
    WorkshopPrioritizationVotingType: WorkshopPrioritizationVotingType;
    WorkshopPhaseParticipantRole: WorkshopPhaseParticipantRole;
    WorkshopQuestionnaireFilter: WorkshopQuestionnaireFilter;
    WorkshopQuestionnaireQuestionType: WorkshopQuestionnaireQuestionType;
    WorkshopQuestionnaireQuestionReport: WorkshopQuestionnaireQuestionReport;
    WorkshopQuestionnaireOption: WorkshopQuestionnaireOption;
    WorkshopQuestionnaireResponse: WorkshopQuestionnaireResponse;
    WorkshopPhaseReport: WorkshopPhaseReport;
    WorkshopCanvasItem: WorkshopCanvasItem;
    WorkshopPhaseContentRowLayout: WorkshopPhaseContentRowLayout;
    WorkshopPhaseContentRowReport: WorkshopPhaseContentRowReport;
    WorkshopWidgetType: WorkshopWidgetType;
    WorkshopWidgetStatus: WorkshopWidgetStatus;
    WorkshopExternalContentType: WorkshopExternalContentType;
    WorkshopWidget: WorkshopWidget;
    WorkshopWidgetReport: WorkshopWidgetReport;
    WorkshopWidgetParticipantStatus: WorkshopWidgetParticipantStatus;
    WorkshopItemCategory: WorkshopItemCategory;
    WorkshopItemCategoryConnection: WorkshopItemCategoryConnection;
    WorkshopItem: WorkshopItem;
    WorkshopVersionHistoryChangeType: WorkshopVersionHistoryChangeType;
    WorkshopItemHistory: WorkshopItemHistory;
    WorkshopRatingPhaseWeightedLevel: WorkshopRatingPhaseWeightedLevel;
    WorkshopRatingPhaseSourceReport: WorkshopRatingPhaseSourceReport;
    WorkshopRatingPhaseCategoryWeight: WorkshopRatingPhaseCategoryWeight;
    WorkshopItemRating: WorkshopItemRating;
    WorkshopItemRatingHistory: WorkshopItemRatingHistory;
    WorkshopPhaseGridBlock: WorkshopPhaseGridBlock;
    WorkshopPhaseComparisonAxis: WorkshopPhaseComparisonAxis;
    WorkshopPhaseComparisonDimension: WorkshopPhaseComparisonDimension;
    WorkshopResultPresentationType: WorkshopResultPresentationType;
    WorkshopResultPhaseSource: WorkshopResultPhaseSource;
    WorkshopWidgetIdeaGenerationTheme: WorkshopWidgetIdeaGenerationTheme;
    WorkshopWidgetIdeaGenerationThemeAnswer: WorkshopWidgetIdeaGenerationThemeAnswer;
    FinancialBatchPreviewReport: FinancialBatchPreviewReport;
    DashboardPageBlockCustomTableGraphSerieType: DashboardPageBlockCustomTableGraphSerieType;
    DashboardPageBlockCustomTableQuery: DashboardPageBlockCustomTableQuery;
    DashboardPageBlockCustomTableColumn: DashboardPageBlockCustomTableColumn;
    DashboardPageBlockCustomTableColumnDefaultFilter: DashboardPageBlockCustomTableColumnDefaultFilter;
    DashboardPageBlockMilestone: DashboardPageBlockMilestone;
    DashboardPageExportSettings: DashboardPageExportSettings;
    DashboardPageBlockImage: DashboardPageBlockImage;
    TrendTopicType: TrendTopicType;
    TrendCategory: TrendCategory;
    TrendScope: TrendScope;
    TrendTopicReport: TrendTopicReport;
    Market: Market;
    MarketFreeNode: MarketFreeNode;
    SystemUserRole: SystemUserRole;
    UserRoleBillingUserType: UserRoleBillingUserType;
    StrategyExecutionType: StrategyExecutionType;
    StrategyPublishingLevel: StrategyPublishingLevel;
    StrategyExecutionCustomFieldReport: StrategyExecutionCustomFieldReport;
    StrategyExecutionCustomEntity: StrategyExecutionCustomEntity;
    StrategyExecutionCustomEntityData: StrategyExecutionCustomEntityData;
    StrategyExecutionCustomFieldOption: StrategyExecutionCustomFieldOption;
    StrategyExecutionCustomFieldLink: StrategyExecutionCustomFieldLink;
    StrategicGoalMilestoneTeamLink: StrategicGoalMilestoneTeamLink;
    StrategicGoalMilestoneSecondaryOwner: StrategicGoalMilestoneSecondaryOwner;
    StrategyExecutionCustomFieldType: StrategyExecutionCustomFieldType;
    StrategyExportItemSettings: StrategyExportItemSettings;
    BusinessReviewSectionType: BusinessReviewSectionType;
    BusinessReviewExcludedSection: BusinessReviewExcludedSection;
    BusinessReviewUserSettings: BusinessReviewUserSettings;
    StrategyBusinessReviewExportSettings: StrategyBusinessReviewExportSettings;
    StrategyExecutionExportSettings: StrategyExecutionExportSettings;
    DialogFlowTemplateReport: DialogFlowTemplateReport;
    DialogFlowTemplateMessageReport: DialogFlowTemplateMessageReport;
    DialogFlowTemplateMessageType: DialogFlowTemplateMessageType;
    DialogFlowTemplateMessageConversionRule: DialogFlowTemplateMessageConversionRule;
    DialogFlowLogicGateValueSource: DialogFlowLogicGateValueSource;
    DialogFlowLogicGateMatchRule: DialogFlowLogicGateMatchRule;
    DialogFlowMultiOptionDataSource: DialogFlowMultiOptionDataSource;
    DialogFlowTemplateMessageOption: DialogFlowTemplateMessageOption;
    DialogFlowSession: DialogFlowSession;
    StrategyProcessReport: StrategyProcessReport;
    StrategyProcessThemeReport: StrategyProcessThemeReport;
    StrategyProcessSubthemeType: StrategyProcessSubthemeType;
    StrategyProcessSubthemeReport: StrategyProcessSubthemeReport;
    StrategyProcessSubthemeLink: StrategyProcessSubthemeLink;
    StrategyProcessPhaseReport: StrategyProcessPhaseReport;
    StrategyProcessPhaseContent: StrategyProcessPhaseContent;
    UserFunctionalityAccessReport: UserFunctionalityAccessReport;
    SpecialMenuItem: SpecialMenuItem;
    SystemUserType: SystemUserType;
    StrategySavedView: StrategySavedView;
    QuickStartReport: QuickStartReport;
    FileTransferHelperEntity: FileTransferHelperEntity;
    UserActivityReport: UserActivityReport;
    UserNotificationUserSettings: UserNotificationUserSettings;
    UserCompanyFunctionalityAccess: UserCompanyFunctionalityAccess;
    DialogScheduleRhytm: DialogScheduleRhytm;
    DialogDistributionChannel: DialogDistributionChannel;
    DialogScheduleFlow: DialogScheduleFlow;
    DialogScheduleUser: DialogScheduleUser;
    DialogScheduleBatchReport: DialogScheduleBatchReport;
    DialogScheduleSettingsReport: DialogScheduleSettingsReport;
    DashboardPageBlockLightReport: DashboardPageBlockLightReport;
    DashboardPageLightReport: DashboardPageLightReport;
    DashboardGroupLightReport: DashboardGroupLightReport;
    WorkspaceMindmapNodeType: WorkspaceMindmapNodeType;
    WorkspaceMindmapNode: WorkspaceMindmapNode;
    WorkspaceMindmapReport: WorkspaceMindmapReport;
    AnalysisCategory: AnalysisCategory;
    AgendaTheme: AgendaTheme;
    AgendaTimeReservation: AgendaTimeReservation;
    AgendaFlag: AgendaFlag;
    AgendaTimeSerieRuleReport: AgendaTimeSerieRuleReport;
    AgendaTimeSerieEvent: AgendaTimeSerieEvent;
    CompanyAccessModule: CompanyAccessModule;
    MicrosoftTeamsBotLinkedConversation: MicrosoftTeamsBotLinkedConversation;
    UserInterfaceTranslationLanguage: UserInterfaceTranslationLanguage;
    UserSyncExcelSettings: UserSyncExcelSettings;
    WorkshopStrategyImportSettings: WorkshopStrategyImportSettings;
    CompanyAccessFunctionalityAddOn: CompanyAccessFunctionalityAddOn;
    AccessFunctionalityAddOn: AccessFunctionalityAddOn;
};
